/**
 * Sourcyness Accessibility Preferences Widget
 * WCAG 2.2 AA Compliant — Supplemental User Preferences Panel
 *
 * DISCLAIMER: This widget is a supplemental preference tool and does NOT
 * make a non-compliant website compliant. It is designed for websites that
 * are already fully ADA and WCAG 2.2 AA compliant at the source code level.
 * It gives visitors extra comfort and personalization controls on top of an
 * already-accessible foundation.
 *
 * @version 2.0.0
 * @license Proprietary — ASA Enterprises Inc / Sourcyness
 * @author Sourcyness Development Team
 */
;(function() {
  'use strict';

  var STORAGE_KEY = 'aw-preferences';
  var PREFIX = 'aw-';
  var WIDGET_ID = 'aw-widget-root';

  /* ================================================================
     DEFAULT PREFERENCES
     Every persisted user choice lives here. Old v1 keys are kept so
     that stored v1 preferences continue to load without breakage.
     ================================================================ */
  var defaults = {
    /* Interface */
    language:        'en',
    oversized:       false,        // XL Oversized Widget
    widgetPosition:  'right',      // 'left' | 'right'

    /* Content adjustments */
    textResize:      100,          // 100 | 125 | 150 | 175 | 200  (Adjust Font Sizing)
    contentScale:    100,          // 100..200 in steps of 10       (Content Scaling / zoom)
    readableFont:    false,        // Readable Font (legible sans stack)
    dyslexiaFont:    false,        // Dyslexia Friendly (OpenDyslexic)
    textSpacing:     'default',    // 'default' | 'comfortable' | 'spacious'
    lineHeight:      'default',    // 'default' | 'tall' | 'taller' | 'tallest'
    letterSpacing:   'default',    // 'default' | 'wide' | 'wider'
    textAlign:       'default',    // 'default' | 'left' | 'center' | 'right'
    highlightTitles: false,        // Highlight Titles (headings)
    textMagnifier:   false,        // Text Magnifier (lens follows pointer)

    /* Links & cursor */
    linkHighlight:   false,        // Highlight Links
    cursorStyle:     'default',    // 'default' | 'big-black' | 'big-white'
    bigCursor:       false,        // back-compat mirror of cursorStyle

    /* Color adjustments */
    contrastMode:    'default',    // 'default'|'dark'|'light'|'high-contrast'|'inverted'|'smart-contrast'
    saturation:      'default',    // 'default' | 'high' | 'low' | 'monochrome'
    contrastBoost:   0,            // 0..3  (Contrast +)
    colorBlindFilter:'none',       // 'none'|'protanopia'|'deuteranopia'|'tritanopia'
    textColor:       '',           // custom hex override ('' = off)
    titleColor:      '',           // custom hex override
    bgColor:         '',           // custom hex override

    /* Orientation adjustments */
    hideImages:      false,
    muteSounds:      false,
    readMode:        false,        // Read Mode (reader view)
    readingGuide:    false,
    readingMask:     false,        // Reading Mask (spotlight band)
    stopAnimations:  false,        // Pause / Stop Animations
    highlightHover:  false,
    highlightFocus:  false,
    tooltips:        false,        // reveal title/aria-label/alt on hover+focus

    /* Assistive */
    screenReader:    false,        // Web Speech text-to-speech
    voiceNavigation: false,        // Speech recognition commands
    keyboardNav:     false,        // enhanced keyboard navigation

    /* Profiles currently active (ids) */
    activeProfiles:  []
  };

  /* ================================================================
     CONFIGURATION (merged with window.AccessibilityWidgetConfig)
     ================================================================ */
  var config = Object.assign({
    position:     'bottom-right', // bottom-right | bottom-left | top-right | top-left
    buttonSize:   56,
    primaryColor: '#0E6B4E',
    accentColor:  '#B94F15',
    zIndex:       99999,

    /* Default UI language; falls back to 'en' if unknown */
    defaultLanguage: 'en',

    /* Keyboard shortcut to open/close the panel.
       'double-shift'  → press Shift twice quickly (default, conflict-free)
       'alt-a'         → Alt + A
       'ctrl-alt-a'    → Ctrl + Alt + A
       false           → disable the open shortcut entirely */
    openShortcut: 'double-shift',
    accessKey:    'a',            // HTML accesskey on the trigger button

    features: {
      /* interface */
      languages: true, profiles: true, oversized: true,
      /* content */
      textResize: true, contentScale: true, readableFont: true, dyslexiaFont: true,
      textSpacing: true, lineHeight: true, letterSpacing: true, textAlign: true,
      highlightTitles: true, textMagnifier: true,
      /* links & cursor */
      linkHighlight: true, bigCursor: true,
      /* color */
      contrastModes: true, saturation: true, contrastBoost: true,
      colorBlind: true, customColors: true,
      /* orientation */
      hideImages: true, muteSounds: true, readMode: true, readingGuide: true,
      readingMask: true, stopAnimations: true, highlightHover: true,
      highlightFocus: true, tooltips: true, pageStructure: true, dictionary: true,
      /* assistive */
      screenReader: true, voiceNavigation: true, keyboardNav: true,
      /* widget */
      widgetPosition: true
    },

    /* Optional list of "useful links" rendered in the navigation aid.
       Each item: { label: 'Contact', href: '#contact' } */
    usefulLinks: null,

    /* Dictionary lookup endpoint (free, no key, CORS-enabled). Set to
       false to disable network lookups entirely. {word} is substituted. */
    dictionaryEndpoint: 'https://api.dictionaryapi.dev/api/v2/entries/en/{word}',

    labels: {
      panelTitle:   null,  // null → use localized string
      resetButton:  null,
      closeButton:  null
    },

    /* ----- Page translation -----
       When true, choosing a language in the panel translates the page's
       visible text AND accessibility attributes (alt / aria-label / title /
       placeholder), then reverts cleanly when the source language is chosen.
       The built-in provider is free and key-less but rate-limited and of
       machine-translation quality; for production, plug in a licensed
       provider via `translateText` (e.g. Google Cloud Translation, DeepL). */
    pageTranslation:     true,
    sourceLanguage:      'en',   // language the page is authored in
    translationEndpoint: 'https://api.mymemory.translated.net/get?q={text}&langpair={from}|{to}',
    translationEmail:    null,   // optional contact email → raises MyMemory's free daily quota
    translateText:       null,   // optional async (texts[], from, to) => Promise<string[]>
    translationMaxUnits: 800,    // safety cap on unique strings translated per run

    onInit: null,
    onChange: null,
    onLanguageChange: null   // (langCode, dir) → also fired so you can swap site i18n bundles
  }, window.AccessibilityWidgetConfig || {});

  /* ================================================================
     INTERNATIONALIZATION
     STRINGS = canonical English keys. LOCALES = per-language overrides.
     Missing keys fall back to English. Languages without a UI map still
     work: they set <html lang>/<dir> and fire onLanguageChange so the
     client can localize page CONTENT via their own translation service.
     ================================================================ */
  var STRINGS = {
    panelTitle:        'Accessibility Menu',
    close:             'Close',
    reset:             'Reset All Settings',
    searchLanguage:    'Search language',
    language:          'Language',
    oversizedWidget:   'Oversized Widget',
    profiles:          'Accessibility Profiles',
    contentAdj:        'Content Adjustments',
    colorAdj:          'Color Adjustments',
    orientationAdj:    'Orientation Adjustments',
    assistiveTech:     'Assistive Technology',
    widgetSettings:    'Widget Settings',
    customize:         'Customize your browsing experience',

    /* profiles */
    p_seizure:         'Seizure Safe',
    p_seizureDesc:     'Reduce motion and visual triggers',
    p_vision:          'Vision Impaired',
    p_visionDesc:      'Improve clarity and contrast',
    p_adhd:            'ADHD Friendly',
    p_adhdDesc:        'Support focus and reduce distractions',
    p_cognitive:       'Reading & Cognitive',
    p_cognitiveDesc:   'Simplify reading and navigation',
    p_keyboard:        'Keyboard Navigation',
    p_keyboardDesc:    'Use the website with the keyboard',
    p_screenReader:    'Screen Reader',
    p_screenReaderDesc:'Optimize for screen readers',
    p_older:           'Older Adults',
    p_olderDesc:       'Enhance visibility and reading comfort',
    p_colorBlind:      'Color Blind',
    p_colorBlindDesc:  'Adjust colors for color vision',
    p_dyslexia:        'Dyslexia',
    p_dyslexiaDesc:    'Easier reading for dyslexia',
    p_motor:           'Motor Impaired',
    p_motorDesc:       'Larger targets and cursor',

    /* content */
    fontSizing:        'Font Sizing',
    contentScaling:    'Content Scaling',
    readableFont:      'Readable Font',
    dyslexiaFont:      'Dyslexia Friendly',
    textSpacing:       'Text Spacing',
    lineHeight:        'Line Height',
    letterSpacing:     'Letter Spacing',
    textAlign:         'Text Align',
    alignLeft:         'Align Left',
    alignCenter:       'Align Center',
    alignRight:        'Align Right',
    highlightTitles:   'Highlight Titles',
    textMagnifier:     'Text Magnifier',
    biggerText:        'Bigger Text',

    /* links & cursor */
    highlightLinks:    'Highlight Links',
    cursor:            'Cursor',
    bigBlackCursor:    'Big Black Cursor',
    bigWhiteCursor:    'Big White Cursor',

    /* color */
    darkContrast:      'Dark Contrast',
    lightContrast:     'Light Contrast',
    highContrast:      'High Contrast',
    smartContrast:     'Smart Contrast',
    invertColors:      'Invert Colors',
    contrastPlus:      'Contrast +',
    highSaturation:    'High Saturation',
    lowSaturation:     'Low Saturation',
    monochrome:        'Monochrome',
    saturation:        'Saturation',
    colorBlindMode:    'Color Blind Filter',
    protanopia:        'Protanopia',
    deuteranopia:      'Deuteranopia',
    tritanopia:        'Tritanopia',
    adjustTextColors:  'Adjust Text Colors',
    adjustTitleColors: 'Adjust Title Colors',
    adjustBgColors:    'Adjust Background Colors',
    cancel:            'Cancel',

    /* orientation */
    hideImages:        'Hide Images',
    muteSounds:        'Mute Sounds',
    readMode:          'Read Mode',
    readingGuide:      'Reading Guide',
    readingMask:       'Reading Mask',
    stopAnimations:    'Stop Animations',
    pauseAnimations:   'Pause Animations',
    highlightHover:    'Highlight Hover',
    highlightFocus:    'Highlight Focus',
    tooltips:          'Tooltips',
    pageStructure:     'Page Structure',
    usefulLinks:       'Useful Links',
    selectOption:      'Select an option',
    dictionary:        'Dictionary',

    /* assistive */
    screenReaderTTS:   'Screen Reader',
    voiceNavigation:   'Voice Navigation',
    keyboardNavigation:'Keyboard Navigation',

    /* page structure dialog */
    headings:          'Headings',
    landmarks:         'Landmarks',
    links:             'Links',
    noItems:           'None found on this page',

    /* speech bar */
    readPage:          'Read Page',
    pause:             'Pause',
    resume:            'Resume',
    stop:              'Stop',
    speed:             'Speed',
    clickToRead:       'Click any text to read it',

    /* misc */
    on:                'ON',
    off:               'OFF',
    defaultLabel:      'Default',
    disclaimer:        'This panel provides additional comfort options. It does not replace built-in accessibility compliance.',
    shortcutHint:      'Tip: press Shift twice to open this menu',
    translating:       'Translating page\u2026',
    translateLimit:    'Translation limit reached \u2014 set up a provider',
    translateNote:     'Translates this page into the selected language.',
    searchSettings:    'Search settings',
    clear:             'Clear',
    noResults:         'No settings match your search',
    left:              'Left',
    right:             'Right',
    oversizedSub:      'Larger panel and controls',
    profilesSub:       'One tap applies a set of adjustments',
    catLanguage:       'Language',
    catDisplay:        'Display',
    catProfiles:       'Profiles',
    catContent:        'Content',
    catColor:          'Color',
    catReading:        'Reading',
    catTools:          'Tools',
    tapToCycle:        'Tap to change',
    comfortable:       'Comfortable',
    spacious:          'Spacious',
    normal:            'Normal',
    enabled:           'enabled',
    disabled:          'disabled'
  };

  /* Language catalogue. dir:'rtl' flags right-to-left scripts.
     `ui:true` means a translated interface map exists in LOCALES. */
  var LANGUAGES = [
    { code:'en',    name:'English (USA)',              flag:'US', dir:'ltr', ui:true  },
    { code:'es',    name:'Español (Spanish)',          flag:'ES', dir:'ltr', ui:true  },
    { code:'fr',    name:'Français (French)',          flag:'FR', dir:'ltr', ui:true  },
    { code:'de',    name:'Deutsch (German)',           flag:'DE', dir:'ltr', ui:true  },
    { code:'pt',    name:'Português (Portuguese)',     flag:'PT', dir:'ltr', ui:true  },
    { code:'it',    name:'Italiano (Italian)',         flag:'IT', dir:'ltr', ui:true  },
    { code:'ar',    name:'العربية (Arabic)',            flag:'AR', dir:'rtl', ui:true  },
    { code:'he',    name:'עברית (Hebrew)',             flag:'HE', dir:'rtl', ui:false },
    { code:'nl',    name:'Nederlands (Dutch)',         flag:'NL', dir:'ltr', ui:false },
    { code:'pl',    name:'Polski (Polish)',            flag:'PL', dir:'ltr', ui:false },
    { code:'ru',    name:'Русский (Russian)',          flag:'RU', dir:'ltr', ui:false },
    { code:'uk',    name:'Українська (Ukrainian)',     flag:'UA', dir:'ltr', ui:false },
    { code:'tr',    name:'Türkçe (Turkish)',           flag:'TR', dir:'ltr', ui:false },
    { code:'hi',    name:'हिन्दी (Hindi)',               flag:'IN', dir:'ltr', ui:false },
    { code:'bn',    name:'বাংলা (Bengali)',             flag:'BD', dir:'ltr', ui:false },
    { code:'ur',    name:'اردو (Urdu)',                flag:'PK', dir:'rtl', ui:false },
    { code:'fa',    name:'فارسی (Persian)',            flag:'IR', dir:'rtl', ui:false },
    { code:'zh',    name:'中文 (Chinese)',              flag:'CN', dir:'ltr', ui:false },
    { code:'ja',    name:'日本語 (Japanese)',           flag:'JP', dir:'ltr', ui:false },
    { code:'ko',    name:'한국어 (Korean)',             flag:'KR', dir:'ltr', ui:false },
    { code:'vi',    name:'Tiếng Việt (Vietnamese)',    flag:'VN', dir:'ltr', ui:false },
    { code:'th',    name:'ไทย (Thai)',                 flag:'TH', dir:'ltr', ui:false },
    { code:'id',    name:'Bahasa Indonesia',           flag:'ID', dir:'ltr', ui:false },
    { code:'fil',   name:'Filipino (Tagalog)',         flag:'PH', dir:'ltr', ui:false },
    { code:'ms',    name:'Bahasa Melayu (Malay)',      flag:'MY', dir:'ltr', ui:false },
    { code:'sw',    name:'Kiswahili (Swahili)',        flag:'KE', dir:'ltr', ui:false },
    { code:'el',    name:'Ελληνικά (Greek)',           flag:'GR', dir:'ltr', ui:false },
    { code:'sv',    name:'Svenska (Swedish)',          flag:'SE', dir:'ltr', ui:false },
    { code:'fi',    name:'Suomi (Finnish)',            flag:'FI', dir:'ltr', ui:false },
    { code:'cs',    name:'Čeština (Czech)',            flag:'CZ', dir:'ltr', ui:false },
    { code:'ro',    name:'Română (Romanian)',          flag:'RO', dir:'ltr', ui:false },
    { code:'hu',    name:'Magyar (Hungarian)',         flag:'HU', dir:'ltr', ui:false }
  ];

  /* High-confidence interface translations. Any key not present here
     falls back to the English STRINGS value above. */
  var LOCALES = {
    es: {
      panelTitle:'Menú de accesibilidad', close:'Cerrar', reset:'Restablecer todo',
      searchLanguage:'Buscar idioma', language:'Idioma', oversizedWidget:'Widget ampliado',
      profiles:'Perfiles de accesibilidad', contentAdj:'Ajustes de contenido',
      colorAdj:'Ajustes de color', orientationAdj:'Ajustes de orientación',
      assistiveTech:'Tecnología de asistencia', widgetSettings:'Ajustes del widget',
      customize:'Personaliza tu navegación',
      fontSizing:'Tamaño de fuente', contentScaling:'Escala del contenido',
      readableFont:'Fuente legible', dyslexiaFont:'Apta para dislexia',
      textSpacing:'Espaciado de texto', lineHeight:'Altura de línea',
      letterSpacing:'Espaciado de letras', textAlign:'Alinear texto',
      alignLeft:'Alinear a la izquierda', alignCenter:'Centrar', alignRight:'Alinear a la derecha',
      highlightTitles:'Resaltar títulos', textMagnifier:'Lupa de texto', biggerText:'Texto más grande',
      highlightLinks:'Resaltar enlaces', cursor:'Cursor',
      bigBlackCursor:'Cursor negro grande', bigWhiteCursor:'Cursor blanco grande',
      darkContrast:'Contraste oscuro', lightContrast:'Contraste claro', highContrast:'Alto contraste',
      smartContrast:'Contraste inteligente', invertColors:'Invertir colores', contrastPlus:'Contraste +',
      highSaturation:'Alta saturación', lowSaturation:'Baja saturación', monochrome:'Monocromo',
      saturation:'Saturación', colorBlindMode:'Filtro daltónico',
      adjustTextColors:'Ajustar color de texto', adjustTitleColors:'Ajustar color de títulos',
      adjustBgColors:'Ajustar color de fondo', cancel:'Cancelar',
      hideImages:'Ocultar imágenes', muteSounds:'Silenciar sonidos', readMode:'Modo lectura',
      readingGuide:'Guía de lectura', readingMask:'Máscara de lectura', stopAnimations:'Detener animaciones',
      pauseAnimations:'Pausar animaciones', highlightHover:'Resaltar al pasar', highlightFocus:'Resaltar foco',
      tooltips:'Descripciones', pageStructure:'Estructura de página', usefulLinks:'Enlaces útiles',
      selectOption:'Selecciona una opción', dictionary:'Diccionario',
      screenReaderTTS:'Lector de pantalla', voiceNavigation:'Navegación por voz',
      keyboardNavigation:'Navegación por teclado',
      headings:'Encabezados', landmarks:'Regiones', links:'Enlaces', noItems:'Ninguno en esta página',
      readPage:'Leer página', pause:'Pausar', resume:'Reanudar', stop:'Detener', speed:'Velocidad',
      clickToRead:'Haz clic en cualquier texto para leerlo',
      on:'SÍ', off:'NO', defaultLabel:'Predeterminado',
      disclaimer:'Este panel ofrece opciones de comodidad. No sustituye al cumplimiento de accesibilidad integrado.',
      shortcutHint:'Consejo: pulsa Mayús dos veces para abrir este menú',
      translating:'Traduciendo la página\u2026', translateNote:'Traduce esta página al idioma seleccionado.',
      enabled:'activado', disabled:'desactivado'
    },
    fr: {
      panelTitle:'Menu d’accessibilité', close:'Fermer', reset:'Tout réinitialiser',
      searchLanguage:'Rechercher une langue', language:'Langue', oversizedWidget:'Widget agrandi',
      profiles:'Profils d’accessibilité', contentAdj:'Ajustements du contenu',
      colorAdj:'Ajustements des couleurs', orientationAdj:'Ajustements d’orientation',
      assistiveTech:'Technologie d’assistance', widgetSettings:'Réglages du widget',
      customize:'Personnalisez votre navigation',
      fontSizing:'Taille de police', contentScaling:'Échelle du contenu',
      readableFont:'Police lisible', dyslexiaFont:'Adapté à la dyslexie',
      textSpacing:'Espacement du texte', lineHeight:'Hauteur de ligne',
      letterSpacing:'Espacement des lettres', textAlign:'Aligner le texte',
      alignLeft:'Aligner à gauche', alignCenter:'Centrer', alignRight:'Aligner à droite',
      highlightTitles:'Surligner les titres', textMagnifier:'Loupe de texte', biggerText:'Texte plus grand',
      highlightLinks:'Surligner les liens', cursor:'Curseur',
      bigBlackCursor:'Grand curseur noir', bigWhiteCursor:'Grand curseur blanc',
      darkContrast:'Contraste sombre', lightContrast:'Contraste clair', highContrast:'Contraste élevé',
      smartContrast:'Contraste intelligent', invertColors:'Inverser les couleurs', contrastPlus:'Contraste +',
      highSaturation:'Saturation élevée', lowSaturation:'Faible saturation', monochrome:'Monochrome',
      saturation:'Saturation', colorBlindMode:'Filtre daltonien',
      adjustTextColors:'Ajuster la couleur du texte', adjustTitleColors:'Ajuster la couleur des titres',
      adjustBgColors:'Ajuster la couleur de fond', cancel:'Annuler',
      hideImages:'Masquer les images', muteSounds:'Couper le son', readMode:'Mode lecture',
      readingGuide:'Guide de lecture', readingMask:'Masque de lecture', stopAnimations:'Arrêter les animations',
      pauseAnimations:'Suspendre les animations', highlightHover:'Surligner au survol', highlightFocus:'Surligner le focus',
      tooltips:'Infobulles', pageStructure:'Structure de la page', usefulLinks:'Liens utiles',
      selectOption:'Sélectionnez une option', dictionary:'Dictionnaire',
      screenReaderTTS:'Lecteur d’écran', voiceNavigation:'Navigation vocale',
      keyboardNavigation:'Navigation au clavier',
      headings:'Titres', landmarks:'Repères', links:'Liens', noItems:'Aucun sur cette page',
      readPage:'Lire la page', pause:'Pause', resume:'Reprendre', stop:'Arrêter', speed:'Vitesse',
      clickToRead:'Cliquez sur un texte pour le lire',
      on:'OUI', off:'NON', defaultLabel:'Par défaut',
      disclaimer:'Ce panneau offre des options de confort. Il ne remplace pas la conformité d’accessibilité intégrée.',
      shortcutHint:'Astuce : appuyez deux fois sur Maj pour ouvrir ce menu',
      translating:'Traduction de la page\u2026', translateNote:'Traduit cette page dans la langue sélectionnée.',
      enabled:'activé', disabled:'désactivé'
    },
    de: {
      panelTitle:'Barrierefreiheitsmenü', close:'Schließen', reset:'Alles zurücksetzen',
      searchLanguage:'Sprache suchen', language:'Sprache', oversizedWidget:'Vergrößertes Widget',
      profiles:'Barrierefreiheitsprofile', contentAdj:'Inhaltsanpassungen',
      colorAdj:'Farbanpassungen', orientationAdj:'Orientierungsanpassungen',
      assistiveTech:'Assistive Technologie', widgetSettings:'Widget-Einstellungen',
      customize:'Passen Sie Ihr Surferlebnis an',
      fontSizing:'Schriftgröße', contentScaling:'Inhaltsskalierung',
      readableFont:'Lesbare Schrift', dyslexiaFont:'Legasthenie-freundlich',
      textSpacing:'Textabstand', lineHeight:'Zeilenhöhe',
      letterSpacing:'Buchstabenabstand', textAlign:'Text ausrichten',
      alignLeft:'Linksbündig', alignCenter:'Zentriert', alignRight:'Rechtsbündig',
      highlightTitles:'Überschriften hervorheben', textMagnifier:'Textlupe', biggerText:'Größerer Text',
      highlightLinks:'Links hervorheben', cursor:'Cursor',
      bigBlackCursor:'Großer schwarzer Cursor', bigWhiteCursor:'Großer weißer Cursor',
      darkContrast:'Dunkler Kontrast', lightContrast:'Heller Kontrast', highContrast:'Hoher Kontrast',
      smartContrast:'Intelligenter Kontrast', invertColors:'Farben umkehren', contrastPlus:'Kontrast +',
      highSaturation:'Hohe Sättigung', lowSaturation:'Geringe Sättigung', monochrome:'Monochrom',
      saturation:'Sättigung', colorBlindMode:'Farbenblind-Filter',
      adjustTextColors:'Textfarbe anpassen', adjustTitleColors:'Titelfarbe anpassen',
      adjustBgColors:'Hintergrundfarbe anpassen', cancel:'Abbrechen',
      hideImages:'Bilder ausblenden', muteSounds:'Töne stummschalten', readMode:'Lesemodus',
      readingGuide:'Leselineal', readingMask:'Lesemaske', stopAnimations:'Animationen stoppen',
      pauseAnimations:'Animationen pausieren', highlightHover:'Hover hervorheben', highlightFocus:'Fokus hervorheben',
      tooltips:'Tooltips', pageStructure:'Seitenstruktur', usefulLinks:'Nützliche Links',
      selectOption:'Option wählen', dictionary:'Wörterbuch',
      screenReaderTTS:'Vorlesefunktion', voiceNavigation:'Sprachnavigation',
      keyboardNavigation:'Tastaturnavigation',
      headings:'Überschriften', landmarks:'Bereiche', links:'Links', noItems:'Keine auf dieser Seite',
      readPage:'Seite vorlesen', pause:'Pause', resume:'Fortsetzen', stop:'Stopp', speed:'Tempo',
      clickToRead:'Klicken Sie auf Text, um ihn vorzulesen',
      on:'AN', off:'AUS', defaultLabel:'Standard',
      disclaimer:'Dieses Panel bietet zusätzliche Komfortoptionen. Es ersetzt keine eingebaute Barrierefreiheit.',
      shortcutHint:'Tipp: Drücken Sie zweimal Umschalt, um dieses Menü zu öffnen',
      translating:'Seite wird übersetzt\u2026', translateNote:'Übersetzt diese Seite in die gewählte Sprache.',
      enabled:'aktiviert', disabled:'deaktiviert'
    },
    pt: {
      panelTitle:'Menu de acessibilidade', close:'Fechar', reset:'Redefinir tudo',
      searchLanguage:'Pesquisar idioma', language:'Idioma', oversizedWidget:'Widget ampliado',
      profiles:'Perfis de acessibilidade', contentAdj:'Ajustes de conteúdo',
      colorAdj:'Ajustes de cor', orientationAdj:'Ajustes de orientação',
      assistiveTech:'Tecnologia assistiva', widgetSettings:'Configurações do widget',
      customize:'Personalize sua navegação',
      fontSizing:'Tamanho da fonte', contentScaling:'Escala do conteúdo',
      readableFont:'Fonte legível', dyslexiaFont:'Compatível com dislexia',
      textSpacing:'Espaçamento do texto', lineHeight:'Altura da linha',
      letterSpacing:'Espaçamento entre letras', textAlign:'Alinhar texto',
      alignLeft:'Alinhar à esquerda', alignCenter:'Centralizar', alignRight:'Alinhar à direita',
      highlightTitles:'Realçar títulos', textMagnifier:'Lupa de texto', biggerText:'Texto maior',
      highlightLinks:'Realçar links', cursor:'Cursor',
      bigBlackCursor:'Cursor preto grande', bigWhiteCursor:'Cursor branco grande',
      darkContrast:'Contraste escuro', lightContrast:'Contraste claro', highContrast:'Alto contraste',
      smartContrast:'Contraste inteligente', invertColors:'Inverter cores', contrastPlus:'Contraste +',
      highSaturation:'Alta saturação', lowSaturation:'Baixa saturação', monochrome:'Monocromático',
      saturation:'Saturação', colorBlindMode:'Filtro para daltonismo',
      adjustTextColors:'Ajustar cor do texto', adjustTitleColors:'Ajustar cor dos títulos',
      adjustBgColors:'Ajustar cor de fundo', cancel:'Cancelar',
      hideImages:'Ocultar imagens', muteSounds:'Silenciar sons', readMode:'Modo de leitura',
      readingGuide:'Guia de leitura', readingMask:'Máscara de leitura', stopAnimations:'Parar animações',
      pauseAnimations:'Pausar animações', highlightHover:'Realçar ao passar', highlightFocus:'Realçar foco',
      tooltips:'Dicas', pageStructure:'Estrutura da página', usefulLinks:'Links úteis',
      selectOption:'Selecione uma opção', dictionary:'Dicionário',
      screenReaderTTS:'Leitor de tela', voiceNavigation:'Navegação por voz',
      keyboardNavigation:'Navegação por teclado',
      headings:'Títulos', landmarks:'Regiões', links:'Links', noItems:'Nenhum nesta página',
      readPage:'Ler página', pause:'Pausar', resume:'Retomar', stop:'Parar', speed:'Velocidade',
      clickToRead:'Clique em qualquer texto para ouvi-lo',
      on:'SIM', off:'NÃO', defaultLabel:'Padrão',
      disclaimer:'Este painel oferece opções de conforto. Não substitui a conformidade de acessibilidade integrada.',
      shortcutHint:'Dica: pressione Shift duas vezes para abrir este menu',
      translating:'Traduzindo a página\u2026', translateNote:'Traduz esta página para o idioma selecionado.',
      enabled:'ativado', disabled:'desativado'
    },
    it: {
      panelTitle:'Menu di accessibilità', close:'Chiudi', reset:'Ripristina tutto',
      searchLanguage:'Cerca lingua', language:'Lingua', oversizedWidget:'Widget ingrandito',
      profiles:'Profili di accessibilità', contentAdj:'Regolazioni del contenuto',
      colorAdj:'Regolazioni dei colori', orientationAdj:'Regolazioni di orientamento',
      assistiveTech:'Tecnologia assistiva', widgetSettings:'Impostazioni del widget',
      customize:'Personalizza la tua navigazione',
      fontSizing:'Dimensione del testo', contentScaling:'Scala del contenuto',
      readableFont:'Carattere leggibile', dyslexiaFont:'Adatto alla dislessia',
      textSpacing:'Spaziatura del testo', lineHeight:'Altezza della riga',
      letterSpacing:'Spaziatura dei caratteri', textAlign:'Allinea testo',
      alignLeft:'Allinea a sinistra', alignCenter:'Centra', alignRight:'Allinea a destra',
      highlightTitles:'Evidenzia titoli', textMagnifier:'Lente di testo', biggerText:'Testo più grande',
      highlightLinks:'Evidenzia link', cursor:'Cursore',
      bigBlackCursor:'Cursore nero grande', bigWhiteCursor:'Cursore bianco grande',
      darkContrast:'Contrasto scuro', lightContrast:'Contrasto chiaro', highContrast:'Alto contrasto',
      smartContrast:'Contrasto intelligente', invertColors:'Inverti colori', contrastPlus:'Contrasto +',
      highSaturation:'Alta saturazione', lowSaturation:'Bassa saturazione', monochrome:'Monocromatico',
      saturation:'Saturazione', colorBlindMode:'Filtro daltonismo',
      adjustTextColors:'Regola colore del testo', adjustTitleColors:'Regola colore dei titoli',
      adjustBgColors:'Regola colore di sfondo', cancel:'Annulla',
      hideImages:'Nascondi immagini', muteSounds:'Disattiva suoni', readMode:'Modalità lettura',
      readingGuide:'Guida di lettura', readingMask:'Maschera di lettura', stopAnimations:'Ferma animazioni',
      pauseAnimations:'Sospendi animazioni', highlightHover:'Evidenzia al passaggio', highlightFocus:'Evidenzia focus',
      tooltips:'Suggerimenti', pageStructure:'Struttura della pagina', usefulLinks:'Link utili',
      selectOption:'Seleziona un’opzione', dictionary:'Dizionario',
      screenReaderTTS:'Lettore dello schermo', voiceNavigation:'Navigazione vocale',
      keyboardNavigation:'Navigazione da tastiera',
      headings:'Intestazioni', landmarks:'Aree', links:'Link', noItems:'Nessuno in questa pagina',
      readPage:'Leggi pagina', pause:'Pausa', resume:'Riprendi', stop:'Ferma', speed:'Velocità',
      clickToRead:'Clicca un testo per ascoltarlo',
      on:'SÌ', off:'NO', defaultLabel:'Predefinito',
      disclaimer:'Questo pannello offre opzioni di comfort. Non sostituisce la conformità di accessibilità integrata.',
      shortcutHint:'Suggerimento: premi Maiusc due volte per aprire questo menu',
      translating:'Traduzione della pagina\u2026', translateNote:'Traduce questa pagina nella lingua selezionata.',
      enabled:'attivato', disabled:'disattivato'
    },
    ar: {
      panelTitle:'قائمة إمكانية الوصول', close:'إغلاق', reset:'إعادة تعيين الكل',
      searchLanguage:'ابحث عن لغة', language:'اللغة', oversizedWidget:'أداة مكبَّرة',
      profiles:'ملفات إمكانية الوصول', contentAdj:'تعديلات المحتوى',
      colorAdj:'تعديلات الألوان', orientationAdj:'تعديلات التوجيه',
      assistiveTech:'التقنية المساعِدة', widgetSettings:'إعدادات الأداة',
      customize:'خصِّص تجربة التصفح',
      fontSizing:'حجم الخط', contentScaling:'تحجيم المحتوى',
      readableFont:'خط مقروء', dyslexiaFont:'مناسب لعسر القراءة',
      textSpacing:'تباعد النص', lineHeight:'ارتفاع السطر',
      letterSpacing:'تباعد الأحرف', textAlign:'محاذاة النص',
      alignLeft:'محاذاة لليسار', alignCenter:'توسيط', alignRight:'محاذاة لليمين',
      highlightTitles:'إبراز العناوين', textMagnifier:'مكبِّر النص', biggerText:'نص أكبر',
      highlightLinks:'إبراز الروابط', cursor:'المؤشر',
      bigBlackCursor:'مؤشر أسود كبير', bigWhiteCursor:'مؤشر أبيض كبير',
      darkContrast:'تباين داكن', lightContrast:'تباين فاتح', highContrast:'تباين عالٍ',
      smartContrast:'تباين ذكي', invertColors:'عكس الألوان', contrastPlus:'تباين +',
      highSaturation:'تشبع عالٍ', lowSaturation:'تشبع منخفض', monochrome:'أحادي اللون',
      saturation:'التشبع', colorBlindMode:'مرشّح عمى الألوان',
      adjustTextColors:'ضبط لون النص', adjustTitleColors:'ضبط لون العناوين',
      adjustBgColors:'ضبط لون الخلفية', cancel:'إلغاء',
      hideImages:'إخفاء الصور', muteSounds:'كتم الأصوات', readMode:'وضع القراءة',
      readingGuide:'دليل القراءة', readingMask:'قناع القراءة', stopAnimations:'إيقاف الحركة',
      pauseAnimations:'إيقاف الرسوم مؤقتًا', highlightHover:'إبراز عند المرور', highlightFocus:'إبراز التركيز',
      tooltips:'تلميحات', pageStructure:'بنية الصفحة', usefulLinks:'روابط مفيدة',
      selectOption:'اختر خيارًا', dictionary:'قاموس',
      screenReaderTTS:'قارئ الشاشة', voiceNavigation:'التنقل الصوتي',
      keyboardNavigation:'التنقل بلوحة المفاتيح',
      headings:'العناوين', landmarks:'المناطق', links:'الروابط', noItems:'لا يوجد في هذه الصفحة',
      readPage:'اقرأ الصفحة', pause:'إيقاف مؤقت', resume:'استئناف', stop:'إيقاف', speed:'السرعة',
      clickToRead:'انقر على أي نص لقراءته',
      on:'تشغيل', off:'إيقاف', defaultLabel:'افتراضي',
      disclaimer:'توفّر هذه اللوحة خيارات راحة إضافية، ولا تحل محل التوافق المدمج لإمكانية الوصول.',
      shortcutHint:'نصيحة: اضغط Shift مرتين لفتح هذه القائمة',
      translating:'جارٍ ترجمة الصفحة\u2026', translateNote:'تترجم هذه الصفحة إلى اللغة المحددة.',
      enabled:'مُفعَّل', disabled:'مُعطَّل'
    }
  };

  /* ================================================================
     RUNTIME STATE
     ================================================================ */
  var prefs       = Object.assign({}, defaults);
  var panelOpen   = false;
  var widgetRoot, triggerBtn, panel, liveRegion, guideEl, maskEl;
  var lastShiftTime = 0;
  var pageHost; /* element page-wide visual filters are applied to (body) */

  /* ================================================================
     TRANSLATION HELPER
     ================================================================ */
  function t(key) {
    var loc = LOCALES[prefs.language];
    if (loc && loc[key] != null) return loc[key];
    return STRINGS[key] != null ? STRINGS[key] : key;
  }

  function langMeta(code) {
    for (var i = 0; i < LANGUAGES.length; i++) {
      if (LANGUAGES[i].code === code) return LANGUAGES[i];
    }
    return LANGUAGES[0];
  }
  /* ================================================================
     PERSISTENCE
     ================================================================ */
  function loadPrefs() {
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved) prefs = Object.assign({}, defaults, JSON.parse(saved));
    } catch (e) { /* localStorage unavailable */ }
    /* Resolve initial language: stored → config.defaultLanguage → 'en' */
    if (!LOCALES[prefs.language] && !langMeta(prefs.language)) prefs.language = 'en';
    /* Migrate v1 bigCursor → cursorStyle */
    if (prefs.bigCursor && prefs.cursorStyle === 'default') prefs.cursorStyle = 'big-black';
    if (!Array.isArray(prefs.activeProfiles)) prefs.activeProfiles = [];
  }

  function savePrefs() {
    prefs.bigCursor = (prefs.cursorStyle !== 'default'); /* keep mirror in sync */
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch (e) {}
    if (typeof config.onChange === 'function') config.onChange(Object.assign({}, prefs));
  }

  function resetPrefs() {
    var lang = prefs.language; /* keep chosen language across reset */
    prefs = Object.assign({}, defaults, { language: lang });
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    applyAll();
    renderControls();
    announce(t('reset') + ' — ' + t('defaultLabel'));
    if (typeof config.onChange === 'function') config.onChange(Object.assign({}, prefs));
  }

  /* ================================================================
     SCREEN-READER ANNOUNCER
     ================================================================ */
  function announce(msg) {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = msg; }, 100);
  }
  function announceToggle(label, on) {
    announce(label + ' ' + (on ? t('enabled') : t('disabled')));
  }

  /* ================================================================
     CSS INJECTION HELPERS
     ================================================================ */
  function injectStyle(id, css) {
    var el = document.getElementById(PREFIX + 'style-' + id);
    if (!el) {
      el = document.createElement('style');
      el.id = PREFIX + 'style-' + id;
      el.setAttribute('data-aw', 'true');
      document.head.appendChild(el);
    }
    el.textContent = css;
  }
  function removeStyle(id) {
    var el = document.getElementById(PREFIX + 'style-' + id);
    if (el) el.remove();
  }

  /* Widget-exclusion guard used by page-wide selectors. Excludes the
     widget root AND all of its descendants. */
  var NW = ':not(#' + WIDGET_ID + '):not(#' + WIDGET_ID + ' *)';

  function scopedSelectors(prefix, elements) {
    return elements.map(function (e) { return prefix + ' ' + e + NW; }).join(',');
  }

  /* ================================================================
     CONTENT — FONT SIZE (existing)  &  CONTENT SCALING (new)
     ================================================================ */
  function applyTextResize() {
    var v = prefs.textResize;
    if (v === 100) {
      removeStyle('text-resize');
      document.documentElement.style.removeProperty('font-size');
    } else {
      document.documentElement.style.fontSize = v + '%';
      injectStyle('text-resize', '#' + WIDGET_ID + '{font-size:16px !important;}');
    }
  }

  function applyContentScale() {
    var v = prefs.contentScale;
    if (v === 100) { removeStyle('content-scale'); return; }
    /* Zoom the document body; the widget lives OUTSIDE <body> (it is a
       child of <html>) so it is never scaled. */
    injectStyle('content-scale', 'body{zoom:' + (v / 100) + ';}');
  }

  /* ================================================================
     CONTENT — READABLE FONT & DYSLEXIA FONT (existing)
     ================================================================ */
  function applyReadableFont() {
    if (prefs.readableFont) {
      injectStyle('readable-font',
        'body ' + bodyAll() + '{font-family:Verdana,Arial,"Helvetica Neue",sans-serif !important;}');
    } else { removeStyle('readable-font'); }
  }

  var dyslexiaLoaded = false;
  function applyDyslexiaFont() {
    if (prefs.dyslexiaFont) {
      if (!dyslexiaLoaded) {
        injectStyle('dyslexia-face',
          "@font-face{font-family:'OpenDyslexic';font-style:normal;font-weight:400;src:url('https://cdn.jsdelivr.net/npm/open-dyslexic@1.0.3/woff/OpenDyslexic-Regular.woff') format('woff');font-display:swap;}" +
          "@font-face{font-family:'OpenDyslexic';font-style:normal;font-weight:700;src:url('https://cdn.jsdelivr.net/npm/open-dyslexic@1.0.3/woff/OpenDyslexic-Bold.woff') format('woff');font-display:swap;}");
        dyslexiaLoaded = true;
      }
      injectStyle('dyslexia-font', 'body ' + bodyAll() + '{font-family:"OpenDyslexic",sans-serif !important;}');
    } else { removeStyle('dyslexia-font'); }
  }

  /* helper: "* :not(widget)" body descendant selector list */
  function bodyAll() { return '*' + NW; }

  /* ================================================================
     CONTENT — TEXT SPACING (existing) / LINE HEIGHT / LETTER SPACING
     ================================================================ */
  function applyTextSpacing() {
    removeStyle('text-spacing');
    var v = prefs.textSpacing;
    if (v === 'default') return;
    var vals = v === 'comfortable'
      ? { lh: '1.8', ls: '0.05em', ws: '0.1em' }
      : { lh: '2.2', ls: '0.1em', ws: '0.15em' };
    injectStyle('text-spacing',
      'body ' + bodyAll() + '{line-height:' + vals.lh + ' !important;letter-spacing:' + vals.ls + ' !important;word-spacing:' + vals.ws + ' !important;}');
  }

  function applyLineHeight() {
    removeStyle('line-height');
    var map = { tall: '1.8', taller: '2.2', tallest: '2.6' };
    var v = map[prefs.lineHeight];
    if (!v) return;
    injectStyle('line-height', 'body ' + bodyAll() + '{line-height:' + v + ' !important;}');
  }

  function applyLetterSpacing() {
    removeStyle('letter-spacing');
    var map = { wide: '0.05em', wider: '0.12em' };
    var v = map[prefs.letterSpacing];
    if (!v) return;
    injectStyle('letter-spacing', 'body ' + bodyAll() + '{letter-spacing:' + v + ' !important;}');
  }

  /* ================================================================
     CONTENT — TEXT ALIGN
     ================================================================ */
  function applyTextAlign() {
    removeStyle('text-align');
    if (prefs.textAlign === 'default') return;
    var els = ['p', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'td', 'th', 'figcaption', 'div'];
    injectStyle('text-align',
      scopedSelectors('body', els) + '{text-align:' + prefs.textAlign + ' !important;}');
  }

  /* ================================================================
     CONTENT — HIGHLIGHT TITLES
     ================================================================ */
  function applyHighlightTitles() {
    if (prefs.highlightTitles) {
      var hs = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
      injectStyle('highlight-titles',
        scopedSelectors('body', hs) + '{background:#FFF3B0 !important;color:#1a1a1a !important;outline:2px solid #B94F15 !important;outline-offset:1px;padding:2px 4px !important;border-radius:3px;}');
    } else { removeStyle('highlight-titles'); }
  }

  /* ================================================================
     LINKS — HIGHLIGHT (existing)
     ================================================================ */
  function applyLinkHighlight() {
    if (prefs.linkHighlight) {
      injectStyle('link-highlight',
        'body a' + NW + '{outline:3px solid ' + config.accentColor + ' !important;outline-offset:2px !important;text-decoration:underline !important;text-decoration-thickness:2px !important;}');
    } else { removeStyle('link-highlight'); }
  }

  /* ================================================================
     CURSOR — BIG BLACK / BIG WHITE (extends existing big cursor)
     ================================================================ */
  function applyCursor() {
    removeStyle('cursor');
    var style = prefs.cursorStyle;
    if (style === 'default') return;
    var fill = style === 'big-white' ? '#ffffff' : '#000000';
    var stroke = style === 'big-white' ? '#000000' : '#ffffff';
    var pointer = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 32 32"><path d="M5 2l20 14H14l6 12-4 2-6-12-5 5z" fill="' + fill + '" stroke="' + stroke + '" stroke-width="1.5" stroke-linejoin="round"/></svg>';
    var hand = '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 32 32"><path d="M16 3a2.5 2.5 0 0 0-2.5 2.5V15H12a2.5 2.5 0 0 0 0 5h.3A2.5 2.5 0 0 0 10 22.5 2.5 2.5 0 0 0 12.5 25h.3a2.5 2.5 0 0 0-.3 1.2A2.5 2.5 0 0 0 15 28.5h5a2.5 2.5 0 0 0 2.5-2.5V15a2.5 2.5 0 0 0-2.5-2.5h-.5V5.5A2.5 2.5 0 0 0 17 3z" fill="' + fill + '" stroke="' + stroke + '" stroke-width="1.2"/></svg>';
    var pUri = 'data:image/svg+xml,' + encodeURIComponent(pointer);
    var hUri = 'data:image/svg+xml,' + encodeURIComponent(hand);
    injectStyle('cursor',
      'html{cursor:url("' + pUri + '") 5 2,default !important;}' +
      'a,button,[role="button"],input,select,textarea,label,[onclick],[tabindex]{cursor:url("' + hUri + '") 14 4,pointer !important;}' +
      '#' + WIDGET_ID + ',#' + WIDGET_ID + ' *{cursor:auto !important;}' +
      '#' + WIDGET_ID + ' button,#' + WIDGET_ID + ' input[type="checkbox"],#' + WIDGET_ID + ' [role="button"]{cursor:pointer !important;}');
  }

  /* ================================================================
     COLOR — CONTRAST MODES (attribute based: dark / light / high)
     ================================================================ */
  function applyContrastMode() {
    removeStyle('contrast');
    document.documentElement.removeAttribute('data-aw-contrast');
    var mode = prefs.contrastMode;
    var attrModes = { dark: 1, light: 1, 'high-contrast': 1 };

    if (mode === 'default' || !attrModes[mode]) { swapLogos('reset'); applyColorFilters(); return; }

    document.documentElement.setAttribute('data-aw-contrast', mode);
    var els = ['body', 'header', 'footer', 'main', 'section', 'div', 'article', 'aside', 'nav', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'li', 'ul', 'ol', 'td', 'th', 'tr', 'table', 'blockquote', 'figcaption', 'label', 'legend', 'fieldset', 'details', 'summary'];
    var borderEls = ['body', 'header', 'footer', 'main', 'section', 'div', 'article', 'aside', 'nav', 'td', 'th', 'tr', 'table', 'fieldset', 'input', 'select', 'textarea'];
    var css = '';

    if (mode === 'high-contrast') {
      var hc = 'html[data-aw-contrast="high-contrast"]';
      css =
        hc + ' body{background:#000000 !important;}' +
        scopedSelectors(hc, els) + '{background:#000000 !important;color:#FFFFFF !important;}' +
        hc + ' a' + NW + '{color:#FFFF00 !important;text-decoration:underline !important;}' +
        scopedSelectors(hc, borderEls) + '{border-color:#FFFFFF !important;}' +
        hc + ' button' + NW + ',' + hc + ' .btn' + NW + '{background:#FFFF00 !important;color:#000000 !important;border-color:#FFFFFF !important;}';
      swapLogos('white');
    } else if (mode === 'light') {
      var lt = 'html[data-aw-contrast="light"]';
      css =
        lt + ' body{background:#FFFFFF !important;}' +
        scopedSelectors(lt, els) + '{background:#FFFFFF !important;color:#1a1a1a !important;}' +
        lt + ' a' + NW + '{color:#0033CC !important;text-decoration:underline !important;}' +
        scopedSelectors(lt, borderEls) + '{border-color:#777777 !important;}';
      swapLogos('green');
    } else if (mode === 'dark') {
      var dk = 'html[data-aw-contrast="dark"]';
      css =
        dk + '{color-scheme:dark;}' +
        scopedSelectors(dk, els) + '{background:#1a1a2e !important;color:#d4d4d8 !important;}' +
        dk + ' a' + NW + '{color:#60a5fa !important;}' +
        dk + ' h1' + NW + ',' + dk + ' h2' + NW + ',' + dk + ' h3' + NW + ',' + dk + ' h4' + NW + '{color:#ffffff !important;}' +
        scopedSelectors(dk, borderEls) + '{border-color:#3f3f46 !important;}';
      swapLogos('white');
    }
    if (css) injectStyle('contrast', css);
    applyColorFilters();
  }

  /* ================================================================
     COLOR — COMPOSED FILTERS (invert / smart-contrast / saturation /
     contrast boost / color-blind correction) applied to <body>.
     ================================================================ */
  function ensureColorBlindDefs() {
    if (document.getElementById(PREFIX + 'cb-defs')) return;
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = PREFIX + 'cb-defs';
    svg.setAttribute('aria-hidden', 'true');
    svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
    /* Daltonization-style correction matrices */
    svg.innerHTML =
      '<defs>' +
      '<filter id="aw-cb-protanopia"><feColorMatrix type="matrix" values="0.567 0.433 0 0 0  0.558 0.442 0 0 0  0 0.242 0.758 0 0  0 0 0 1 0"/></filter>' +
      '<filter id="aw-cb-deuteranopia"><feColorMatrix type="matrix" values="0.625 0.375 0 0 0  0.7 0.3 0 0 0  0 0.3 0.7 0 0  0 0 0 1 0"/></filter>' +
      '<filter id="aw-cb-tritanopia"><feColorMatrix type="matrix" values="0.95 0.05 0 0 0  0 0.433 0.567 0 0  0 0.475 0.525 0 0  0 0 0 1 0"/></filter>' +
      '</defs>';
    document.body.appendChild(svg);
  }

  function applyColorFilters() {
    var parts = [];
    if (prefs.contrastMode === 'inverted') parts.push('invert(1) hue-rotate(180deg)');
    if (prefs.contrastMode === 'smart-contrast') parts.push('contrast(1.3) saturate(1.1)');
    if (prefs.saturation === 'high') parts.push('saturate(2)');
    else if (prefs.saturation === 'low') parts.push('saturate(0.45)');
    else if (prefs.saturation === 'monochrome') parts.push('grayscale(1)');
    if (prefs.contrastBoost > 0) parts.push('contrast(' + (1 + prefs.contrastBoost * 0.2) + ')');
    if (prefs.colorBlindFilter !== 'none') {
      ensureColorBlindDefs();
      parts.push('url(#' + PREFIX + 'cb-' + prefs.colorBlindFilter + ')');
    }

    if (!parts.length) { removeStyle('color-filters'); return; }
    var filter = parts.join(' ');
    var css = 'body{filter:' + filter + ' !important;}';
    /* Counter-invert media so photos read correctly under Invert mode */
    if (prefs.contrastMode === 'inverted') {
      css += 'body img' + NW + ',body video' + NW + ',body picture' + NW + ',body canvas' + NW + ',body [style*="background-image"]' + NW + '{filter:invert(1) hue-rotate(180deg) !important;}';
    }
    injectStyle('color-filters', css);
  }

  /* ================================================================
     COLOR — CUSTOM TEXT / TITLE / BACKGROUND COLOR OVERRIDES
     ================================================================ */
  function applyCustomColors() {
    removeStyle('custom-colors');
    var css = '';
    if (prefs.textColor) {
      var textEls = ['p', 'span', 'li', 'td', 'th', 'a', 'label', 'blockquote', 'figcaption', 'div'];
      css += scopedSelectors('body', textEls) + '{color:' + prefs.textColor + ' !important;}';
    }
    if (prefs.titleColor) {
      var titleEls = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
      css += scopedSelectors('body', titleEls) + '{color:' + prefs.titleColor + ' !important;}';
    }
    if (prefs.bgColor) {
      var bgEls = ['body', 'header', 'footer', 'main', 'section', 'article', 'aside', 'div'];
      css += scopedSelectors('body', bgEls) + '{background-color:' + prefs.bgColor + ' !important;}';
    }
    if (css) injectStyle('custom-colors', css);
  }

  /* ================================================================
     LOGO SWAPPING (existing)
     ================================================================ */
  function swapLogos(mode) {
    document.querySelectorAll('img').forEach(function (img) {
      var src = img.getAttribute('src') || '';
      if (src.indexOf('logo') === -1) return;
      if (img.closest('#' + WIDGET_ID)) return;
      if (!img.getAttribute('data-aw-original-src')) img.setAttribute('data-aw-original-src', src);
      if (mode === 'green') img.setAttribute('src', src.replace('logo-white', 'logo-green'));
      else if (mode === 'white') img.setAttribute('src', src.replace('logo-green', 'logo-white'));
      else {
        var orig = img.getAttribute('data-aw-original-src');
        if (orig) img.setAttribute('src', orig);
      }
    });
  }

  /* ================================================================
     ORIENTATION — HIDE IMAGES (existing)
     ================================================================ */
  function applyHideImages() {
    if (prefs.hideImages) {
      injectStyle('hide-images',
        'body img' + NW + ',body picture' + NW + ',body figure' + NW + ',body svg' + NW + '{opacity:0 !important;}' +
        'body [style*="background-image"]' + NW + '{background-image:none !important;}');
    } else { removeStyle('hide-images'); }
  }

  /* ================================================================
     ORIENTATION — MUTE SOUNDS
     ================================================================ */
  var muteObserver = null;
  function applyMuteSounds() {
    if (prefs.muteSounds) {
      document.querySelectorAll('audio,video').forEach(function (m) {
        try { m.muted = true; } catch (e) {}
      });
      if (!muteObserver && window.MutationObserver) {
        muteObserver = new MutationObserver(function () {
          document.querySelectorAll('audio,video').forEach(function (m) { try { m.muted = true; } catch (e) {} });
        });
        muteObserver.observe(document.documentElement, { childList: true, subtree: true });
      }
    } else {
      if (muteObserver) { muteObserver.disconnect(); muteObserver = null; }
      document.querySelectorAll('audio,video').forEach(function (m) {
        if (m.closest('#' + WIDGET_ID)) return;
        try { m.muted = false; } catch (e) {}
      });
    }
  }

  /* ================================================================
     ORIENTATION — STOP / PAUSE ANIMATIONS (existing)
     ================================================================ */
  function applyStopAnimations() {
    if (prefs.stopAnimations) {
      injectStyle('stop-animations',
        '*' + NW + ',*' + NW + '::before,*' + NW + '::after{animation-duration:0.001s !important;animation-iteration-count:1 !important;transition-duration:0.001s !important;scroll-behavior:auto !important;}');
      document.querySelectorAll('video[autoplay]').forEach(function (v) { try { v.pause(); } catch (e) {} });
    } else {
      removeStyle('stop-animations');
      document.querySelectorAll('video[autoplay]').forEach(function (v) { try { v.play(); } catch (e) {} });
    }
  }

  /* ================================================================
     ORIENTATION — HIGHLIGHT HOVER / FOCUS
     ================================================================ */
  function applyHighlightHover() {
    if (prefs.highlightHover) {
      injectStyle('highlight-hover',
        'body a' + NW + ':hover,body button' + NW + ':hover,body [role="button"]' + NW + ':hover,body input' + NW + ':hover,body select' + NW + ':hover,body textarea' + NW + ':hover,body label' + NW + ':hover{outline:3px solid #1B6FB3 !important;outline-offset:2px !important;background:rgba(27,111,179,0.08) !important;}');
    } else { removeStyle('highlight-hover'); }
  }
  function applyHighlightFocus() {
    if (prefs.highlightFocus) {
      injectStyle('highlight-focus',
        'body *' + NW + ':focus,body *' + NW + ':focus-visible{outline:4px solid #B94F15 !important;outline-offset:2px !important;box-shadow:0 0 0 6px rgba(185,79,21,0.30) !important;border-radius:3px !important;}');
    } else { removeStyle('highlight-focus'); }
  }

  /* ================================================================
     ORIENTATION — KEYBOARD NAVIGATION (enhanced)
     ================================================================ */
  function applyKeyboardNav() {
    if (prefs.keyboardNav) {
      injectStyle('keyboard-nav',
        'body a' + NW + ':focus,body button' + NW + ':focus,body input' + NW + ':focus,body select' + NW + ':focus,body textarea' + NW + ':focus,body [tabindex]' + NW + ':focus,body [role="button"]' + NW + ':focus{outline:4px solid #0E6B4E !important;outline-offset:3px !important;box-shadow:0 0 0 7px rgba(14,107,78,0.25) !important;}' +
        '#' + PREFIX + 'skip-link:focus{transform:translateY(0) !important;}');
      ensureSkipLink();
    } else {
      removeStyle('keyboard-nav');
      var sl = document.getElementById(PREFIX + 'skip-link');
      if (sl) sl.style.display = 'none';
    }
  }
  function ensureSkipLink() {
    var sl = document.getElementById(PREFIX + 'skip-link');
    if (!sl) {
      sl = document.createElement('a');
      sl.id = PREFIX + 'skip-link';
      sl.href = '#';
      sl.textContent = 'Skip to main content';
      sl.style.cssText = 'position:fixed;top:8px;left:8px;z-index:' + (config.zIndex + 5) + ';background:' + config.primaryColor + ';color:#fff;padding:10px 16px;border-radius:6px;font:600 14px/1.2 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;text-decoration:none;transform:translateY(-150%);transition:transform 0.2s;';
      sl.addEventListener('click', function (e) {
        e.preventDefault();
        var main = document.querySelector('main,[role="main"],#main,#content');
        if (main) { main.setAttribute('tabindex', '-1'); main.focus(); main.scrollIntoView(); }
      });
      document.documentElement.appendChild(sl);
    }
    sl.style.display = 'block';
  }
  /* ================================================================
     ORIENTATION — READING GUIDE (existing) — thin ruled highlight
     ================================================================ */
  function createGuide() {
    if (guideEl) return;
    guideEl = document.createElement('div');
    guideEl.id = PREFIX + 'reading-guide';
    guideEl.setAttribute('aria-hidden', 'true');
    guideEl.style.cssText = 'position:fixed;left:0;right:0;height:2.4em;pointer-events:none;z-index:' + (config.zIndex - 1) + ';display:none;transition:top .05s linear;';
    var bar = document.createElement('div');
    bar.style.cssText = 'width:100%;height:100%;background:rgba(255,213,79,0.18);border-top:2px solid rgba(255,160,0,0.7);border-bottom:2px solid rgba(255,160,0,0.7);';
    guideEl.appendChild(bar);
    document.documentElement.appendChild(guideEl);
  }
  function moveGuide(y) {
    if (!guideEl) return;
    guideEl.style.top = Math.max(0, y - guideEl.offsetHeight / 2) + 'px';
  }
  function onGuideMove(e) {
    var y = e.touches ? e.touches[0].clientY : e.clientY;
    requestAnimationFrame(function () { moveGuide(y); });
  }
  function applyReadingGuide() {
    createGuide();
    if (prefs.readingGuide) {
      guideEl.style.display = 'block';
      document.addEventListener('mousemove', onGuideMove, { passive: true });
      document.addEventListener('touchmove', onGuideMove, { passive: true });
    } else {
      guideEl.style.display = 'none';
      document.removeEventListener('mousemove', onGuideMove);
      document.removeEventListener('touchmove', onGuideMove);
    }
  }

  /* ================================================================
     ORIENTATION — READING MASK (spotlight: dims all but a band)
     ================================================================ */
  function createMask() {
    if (maskEl) return;
    maskEl = document.createElement('div');
    maskEl.id = PREFIX + 'reading-mask';
    maskEl.setAttribute('aria-hidden', 'true');
    maskEl.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:' + (config.zIndex - 1) + ';display:none;';
    var top = document.createElement('div');
    top.className = PREFIX + 'mask-top';
    top.style.cssText = 'position:absolute;left:0;right:0;top:0;height:0;background:rgba(0,0,0,0.72);';
    var bot = document.createElement('div');
    bot.className = PREFIX + 'mask-bottom';
    bot.style.cssText = 'position:absolute;left:0;right:0;bottom:0;height:0;background:rgba(0,0,0,0.72);';
    maskEl.appendChild(top);
    maskEl.appendChild(bot);
    document.documentElement.appendChild(maskEl);
  }
  function moveMask(y) {
    if (!maskEl) return;
    var band = 120;
    var top = maskEl.querySelector('.' + PREFIX + 'mask-top');
    var bot = maskEl.querySelector('.' + PREFIX + 'mask-bottom');
    top.style.height = Math.max(0, y - band / 2) + 'px';
    bot.style.height = Math.max(0, window.innerHeight - y - band / 2) + 'px';
  }
  function onMaskMove(e) {
    var y = e.touches ? e.touches[0].clientY : e.clientY;
    requestAnimationFrame(function () { moveMask(y); });
  }
  function applyReadingMask() {
    createMask();
    if (prefs.readingMask) {
      maskEl.style.display = 'block';
      moveMask(window.innerHeight / 2);
      document.addEventListener('mousemove', onMaskMove, { passive: true });
      document.addEventListener('touchmove', onMaskMove, { passive: true });
    } else {
      maskEl.style.display = 'none';
      document.removeEventListener('mousemove', onMaskMove);
      document.removeEventListener('touchmove', onMaskMove);
    }
  }

  /* ================================================================
     CONTENT — TEXT MAGNIFIER (lens shows enlarged text under pointer)
     ================================================================ */
  var magEl = null;
  function createMagnifier() {
    if (magEl) return;
    magEl = document.createElement('div');
    magEl.id = PREFIX + 'magnifier';
    magEl.setAttribute('aria-hidden', 'true');
    magEl.style.cssText = 'position:fixed;max-width:520px;background:#111;color:#fff;font:600 26px/1.35 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;padding:14px 18px;border-radius:10px;box-shadow:0 8px 30px rgba(0,0,0,.35);pointer-events:none;z-index:' + (config.zIndex + 3) + ';display:none;';
    document.documentElement.appendChild(magEl);
  }
  function magCaretNode(x, y) {
    try {
      if (document.caretRangeFromPoint) { var r = document.caretRangeFromPoint(x, y); return r ? r.startContainer : null; }
      if (document.caretPositionFromPoint) { var p = document.caretPositionFromPoint(x, y); return p ? p.offsetNode : null; }
    } catch (e) {}
    return null;
  }
  function onMagnifierMove(e) {
    if (!magEl) return;
    var x = e.clientX, y = e.clientY;
    var el = document.elementFromPoint(x, y);
    if (!el || el.closest('#' + WIDGET_ID)) { magEl.style.display = 'none'; return; }
    var text = '';
    /* Most precise: the exact text run under the pointer (works for text directly
       inside a <div>, inside <b>/<strong>, table cells, etc.). */
    var cn = magCaretNode(x, y);
    if (cn) {
      var owner = cn.nodeType === 3 ? cn.parentElement : cn;
      if (owner && owner.closest('#' + WIDGET_ID)) { magEl.style.display = 'none'; return; }
      if (cn.nodeType === 3 && cn.textContent && cn.textContent.trim().length >= 2) text = cn.textContent.trim();
      else if (owner && owner.textContent) text = owner.textContent.trim();
    }
    /* Fallback: nearest text-bearing element (broadened to inline + div). */
    if (!text) {
      var node = el.closest('p,li,a,h1,h2,h3,h4,h5,h6,span,b,strong,em,i,u,small,mark,td,th,dd,dt,blockquote,figcaption,caption,label,button,summary,legend,div');
      text = (node ? node.textContent : el.textContent || '').trim();
    }
    if (!text || text.length < 2) { magEl.style.display = 'none'; return; }
    if (text.length > 240) text = text.slice(0, 240) + '\u2026';
    magEl.textContent = text;
    magEl.style.display = 'block';
    var mw = magEl.offsetWidth, mh = magEl.offsetHeight;
    var left = Math.min(x + 18, window.innerWidth - mw - 10);
    var top = (y + mh + 28 > window.innerHeight) ? (y - mh - 18) : (y + 22);
    magEl.style.left = Math.max(10, left) + 'px';
    magEl.style.top = Math.max(10, top) + 'px';
  }
  function applyTextMagnifier() {
    createMagnifier();
    if (prefs.textMagnifier) {
      document.addEventListener('mousemove', onMagnifierMove, { passive: true });
    } else {
      document.removeEventListener('mousemove', onMagnifierMove);
      magEl.style.display = 'none';
    }
  }

  /* ================================================================
     ORIENTATION — TOOLTIPS (reveal title / aria-label / alt on focus)
     ================================================================ */
  var tipEl = null;
  function ensureTip() {
    if (tipEl) return;
    tipEl = document.createElement('div');
    tipEl.id = PREFIX + 'tooltip';
    tipEl.setAttribute('role', 'tooltip');
    tipEl.style.cssText = 'position:fixed;background:#1a1a2e;color:#fff;font:500 13px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;padding:6px 10px;border-radius:6px;max-width:280px;box-shadow:0 4px 16px rgba(0,0,0,.3);pointer-events:none;z-index:' + (config.zIndex + 4) + ';display:none;';
    document.documentElement.appendChild(tipEl);
  }
  function tipText(el) {
    var node = el.closest('[title],[aria-label],[alt],img');
    if (!node || node.closest('#' + WIDGET_ID)) return '';
    return node.getAttribute('title') || node.getAttribute('aria-label') || node.getAttribute('alt') || '';
  }
  function showTipAt(el, x, y) {
    var txt = tipText(el);
    if (!txt) { hideTip(); return; }
    tipEl.textContent = txt;
    tipEl.style.display = 'block';
    var tw = tipEl.offsetWidth, th = tipEl.offsetHeight;
    tipEl.style.left = Math.min(x + 14, window.innerWidth - tw - 8) + 'px';
    tipEl.style.top = Math.max(8, y - th - 12) + 'px';
  }
  function hideTip() { if (tipEl) tipEl.style.display = 'none'; }
  function onTipHover(e) { ensureTip(); showTipAt(e.target, e.clientX, e.clientY); }
  function onTipFocus(e) {
    ensureTip();
    var r = e.target.getBoundingClientRect();
    showTipAt(e.target, r.left, r.bottom + (tipEl.offsetHeight || 0) + 12);
  }
  function applyTooltips() {
    ensureTip();
    if (prefs.tooltips) {
      document.addEventListener('mouseover', onTipHover, true);
      document.addEventListener('mouseout', hideTip, true);
      document.addEventListener('focusin', onTipFocus, true);
      document.addEventListener('focusout', hideTip, true);
    } else {
      document.removeEventListener('mouseover', onTipHover, true);
      document.removeEventListener('mouseout', hideTip, true);
      document.removeEventListener('focusin', onTipFocus, true);
      document.removeEventListener('focusout', hideTip, true);
      hideTip();
    }
  }

  /* ================================================================
     ORIENTATION — READ MODE (reader view: declutter to main content)
     ================================================================ */
  function applyReadMode() {
    if (prefs.readMode) {
      var main = document.querySelector('main,[role="main"],article');
      var keepSel = 'main,[role="main"],article';
      if (!main) { /* nothing obvious to isolate; gracefully apply gentle reading layout */
        injectStyle('read-mode',
          'body ' + bodyAll() + '{max-width:none;}' +
          'body p' + NW + ',body li' + NW + '{max-width:70ch !important;line-height:1.8 !important;}');
        return;
      }
      injectStyle('read-mode',
        'body header' + NW + ',body footer' + NW + ',body nav' + NW + ',body aside' + NW + ',body [role="banner"]' + NW + ',body [role="navigation"]' + NW + ',body [role="complementary"]' + NW + ',body .ad' + NW + ',body .ads' + NW + ',body .sidebar' + NW + '{display:none !important;}' +
        'body ' + keepSel.split(',').map(function (s) { return s + NW; }).join(',') + '{max-width:740px !important;margin:0 auto !important;float:none !important;padding:24px !important;background:#fffdf7 !important;color:#1a1a1a !important;font-size:1.08rem !important;line-height:1.85 !important;}');
    } else { removeStyle('read-mode'); }
  }

  /* ================================================================
     ASSISTIVE — SCREEN READER / TEXT-TO-SPEECH (existing, localized)
     ================================================================ */
  var speechBar = null, speechSpeed = 1, speechActive = false, highlightedEl = null;
  function speechSupported() { return 'speechSynthesis' in window; }
  function stopSpeech() { if (speechSupported()) window.speechSynthesis.cancel(); clearHighlight(); }
  function clearHighlight() {
    if (highlightedEl) {
      highlightedEl.style.removeProperty('outline');
      highlightedEl.style.removeProperty('outline-offset');
      highlightedEl.style.removeProperty('background-color');
      highlightedEl = null;
    }
  }
  function speakText(text, el) {
    if (!speechSupported() || !text.trim()) return;
    stopSpeech();
    var u = new SpeechSynthesisUtterance(text.trim());
    u.rate = speechSpeed;
    u.lang = prefs.language || document.documentElement.lang || 'en';
    if (el) {
      highlightedEl = el;
      el.style.outline = '3px solid ' + config.accentColor;
      el.style.outlineOffset = '2px';
      el.style.backgroundColor = 'rgba(232,114,42,0.08)';
    }
    u.onend = function () { clearHighlight(); updateSpeechBar(); };
    u.onerror = function () { clearHighlight(); updateSpeechBar(); };
    window.speechSynthesis.speak(u);
    updateSpeechBar();
  }
  function readPage() {
    if (!speechSupported()) return;
    var main = document.querySelector('main') || document.body;
    var out = [];
    main.querySelectorAll('h1,h2,h3,h4,h5,h6,p,li,td,th,blockquote,figcaption,label,a').forEach(function (el) {
      if (el.closest('#' + WIDGET_ID)) return;
      var x = el.textContent.trim();
      if (x && x.length > 1) out.push(x);
    });
    if (out.length) speakText(out.join('. '), null);
  }
  function onClickToRead(e) {
    if (!prefs.screenReader || !speechActive) return;
    var el = e.target.closest('h1,h2,h3,h4,h5,h6,p,li,span,a,td,th,blockquote,figcaption,label,div');
    if (!el || el.closest('#' + WIDGET_ID)) return;
    var text = el.textContent.trim();
    if (text && text.length > 1) { e.preventDefault(); e.stopPropagation(); speakText(text, el); }
  }
  function createSpeechBar() {
    if (speechBar) return;
    speechBar = document.createElement('div');
    speechBar.id = PREFIX + 'speech-bar';
    speechBar.className = PREFIX + 'speech-bar';
    speechBar.setAttribute('aria-label', t('screenReaderTTS'));
    speechBar.setAttribute('role', 'toolbar');
    (widgetRoot || document.documentElement).appendChild(speechBar);
  }
  function updateSpeechBar() {
    if (!speechBar) return;
    var speaking = speechSupported() && window.speechSynthesis.speaking;
    var paused = speechSupported() && window.speechSynthesis.paused;
    speechBar.innerHTML = '';
    var readBtn = document.createElement('button');
    readBtn.type = 'button';
    readBtn.className = PREFIX + 'sbtn ' + PREFIX + 'sbtn-primary';
    readBtn.setAttribute('aria-label', t('readPage'));
    readBtn.innerHTML = ic('speaker') + '<span>' + escapeHtml(t('readPage')) + '</span>';
    readBtn.addEventListener('click', readPage);
    speechBar.appendChild(readBtn);
    if (speaking || paused) {
      var pb = document.createElement('button');
      pb.type = 'button';
      pb.className = PREFIX + 'sbtn ' + PREFIX + 'sbtn-icon';
      if (paused) { pb.innerHTML = ic('play'); pb.setAttribute('aria-label', t('resume')); pb.addEventListener('click', function () { window.speechSynthesis.resume(); updateSpeechBar(); }); }
      else { pb.innerHTML = ic('pauseBars'); pb.setAttribute('aria-label', t('pause')); pb.addEventListener('click', function () { window.speechSynthesis.pause(); updateSpeechBar(); }); }
      speechBar.appendChild(pb);
      var sb = document.createElement('button');
      sb.type = 'button'; sb.setAttribute('aria-label', t('stop')); sb.innerHTML = ic('stopSq');
      sb.className = PREFIX + 'sbtn ' + PREFIX + 'sbtn-icon';
      sb.addEventListener('click', function () { stopSpeech(); updateSpeechBar(); });
      speechBar.appendChild(sb);
    }
    var lab = document.createElement('span');
    lab.style.cssText = 'font-size:11px;color:var(--aw-muted);margin-left:4px;';
    lab.textContent = t('speed') + ':';
    speechBar.appendChild(lab);
    [0.5, 0.75, 1, 1.25, 1.5, 2].forEach(function (spd) {
      var b = document.createElement('button');
      b.type = 'button'; b.setAttribute('aria-label', t('speed') + ' ' + spd + 'x'); b.textContent = spd + 'x';
      var on = speechSpeed === spd;
      b.style.cssText = 'padding:4px 8px;border:1.5px solid ' + (on ? config.primaryColor : 'var(--aw-hair-2)') + ';background:' + (on ? config.primaryColor : 'var(--aw-surface-2)') + ';color:' + (on ? '#fff' : 'var(--aw-ink-2)') + ';border-radius:8px;cursor:pointer;font-size:11px;font-weight:' + (on ? '700' : '600') + ';';
      b.addEventListener('click', function () { speechSpeed = spd; updateSpeechBar(); });
      speechBar.appendChild(b);
    });
    var hint = document.createElement('span');
    hint.style.cssText = 'font-size:10px;color:var(--aw-muted);margin-left:4px;font-style:italic;';
    hint.textContent = t('clickToRead');
    speechBar.appendChild(hint);
  }
  function applyScreenReader() {
    if (!speechSupported()) return;
    createSpeechBar();
    if (prefs.screenReader) {
      speechActive = true;
      speechBar.style.display = 'flex';
      updateSpeechBar();
      document.addEventListener('click', onClickToRead, true);
    } else {
      speechActive = false;
      stopSpeech();
      speechBar.style.display = 'none';
      document.removeEventListener('click', onClickToRead, true);
    }
  }

  /* ================================================================
     ASSISTIVE — VOICE NAVIGATION (Speech Recognition; Chrome/Edge)
     Simple, safe command set. Gracefully no-ops where unsupported.
     ================================================================ */
  var recognition = null, voiceActive = false;
  function voiceSupported() { return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window; }
  function applyVoiceNavigation() {
    if (!voiceSupported()) { prefs.voiceNavigation = false; return; }
    if (prefs.voiceNavigation && !voiceActive) startVoice();
    else if (!prefs.voiceNavigation && voiceActive) stopVoice();
  }
  function startVoice() {
    var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = prefs.language || 'en';
    recognition.onresult = function (ev) {
      var cmd = ev.results[ev.results.length - 1][0].transcript.toLowerCase().trim();
      handleVoiceCommand(cmd);
    };
    recognition.onend = function () { if (voiceActive) { try { recognition.start(); } catch (e) {} } };
    try { recognition.start(); voiceActive = true; announce('Voice navigation ' + t('enabled')); }
    catch (e) { voiceActive = false; }
  }
  function stopVoice() {
    voiceActive = false;
    if (recognition) { try { recognition.stop(); } catch (e) {} recognition = null; }
  }
  function handleVoiceCommand(cmd) {
    if (cmd.indexOf('scroll down') > -1) window.scrollBy({ top: window.innerHeight * 0.8, behavior: 'smooth' });
    else if (cmd.indexOf('scroll up') > -1) window.scrollBy({ top: -window.innerHeight * 0.8, behavior: 'smooth' });
    else if (cmd.indexOf('top') > -1) window.scrollTo({ top: 0, behavior: 'smooth' });
    else if (cmd.indexOf('bottom') > -1) window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    else if (cmd.indexOf('read page') > -1) readPage();
    else if (cmd.indexOf('stop reading') > -1) stopSpeech();
    else if (cmd.indexOf('open menu') > -1 || cmd.indexOf('accessibility') > -1) openPanel();
    else if (cmd.indexOf('close menu') > -1) closePanel();
    else if (cmd.indexOf('go back') > -1) history.back();
    else if (cmd.indexOf('reload') > -1) location.reload();
  }

  /* ================================================================
     ORIENTATION — DICTIONARY (select a word → definition popup)
     ================================================================ */
  var dictPopup = null;
  function ensureDictPopup() {
    if (dictPopup) return;
    dictPopup = document.createElement('div');
    dictPopup.id = PREFIX + 'dict-popup';
    dictPopup.setAttribute('role', 'dialog');
    dictPopup.style.cssText = 'position:fixed;max-width:320px;background:#fff;color:#1a1a2e;border:1px solid #d1d5db;border-radius:10px;box-shadow:0 8px 30px rgba(0,0,0,.25);padding:14px 16px;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;z-index:' + (config.zIndex + 4) + ';display:none;';
    document.documentElement.appendChild(dictPopup);
  }
  function onDictSelect(e) {
    if (!prefs.dictionary || !config.dictionaryEndpoint) return;
    /* Ignore clicks inside the popup (e.g. the Close button) or the widget,
       otherwise this mouseup would immediately re-open the lookup. */
    if (e && e.target && e.target.nodeType === 1) {
      if (dictPopup && dictPopup.contains(e.target)) return;
      if (e.target.closest && e.target.closest('#' + WIDGET_ID)) return;
    }
    var sel = (window.getSelection ? window.getSelection().toString() : '').trim();
    if (!sel || sel.split(/\s+/).length !== 1 || sel.length > 40) {
      if (dictPopup) dictPopup.style.display = 'none';   /* click elsewhere dismisses */
      return;
    }
    ensureDictPopup();
    var word = sel.replace(/[^A-Za-z'-]/g, '');
    if (!word) { dictPopup.style.display = 'none'; return; }
    dictPopup.innerHTML = '<strong>' + word + '</strong><br><span style="color:#636363">…</span>';
    positionDict();
    dictPopup.style.display = 'block';
    fetch(config.dictionaryEndpoint.replace('{word}', encodeURIComponent(word)))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data[0]) { dictPopup.innerHTML = '<strong>' + word + '</strong><br><span style="color:#636363">No definition found.</span>'; return; }
        var entry = data[0];
        var phon = entry.phonetic || '';
        var defs = '';
        (entry.meanings || []).slice(0, 2).forEach(function (m) {
          var d = (m.definitions && m.definitions[0]) ? m.definitions[0].definition : '';
          if (d) defs += '<div style="margin-top:6px"><em style="color:' + config.primaryColor + '">' + (m.partOfSpeech || '') + '</em> ' + escapeHtml(d) + '</div>';
        });
        dictPopup.innerHTML = '<strong style="font-size:16px">' + escapeHtml(word) + '</strong> <span style="color:#888">' + escapeHtml(phon) + '</span>' + defs +
          '<button type="button" id="' + PREFIX + 'dict-close" style="margin-top:10px;border:1px solid #d1d5db;background:#f5f5f5;border-radius:6px;padding:4px 10px;cursor:pointer;font-size:12px">' + t('close') + '</button>';
        var c = document.getElementById(PREFIX + 'dict-close');
        if (c) c.addEventListener('click', function () {
          dictPopup.style.display = 'none';
          try { var s = window.getSelection && window.getSelection(); if (s && s.removeAllRanges) s.removeAllRanges(); } catch (err) {}
        });
      })
      .catch(function () { dictPopup.innerHTML = '<strong>' + word + '</strong><br><span style="color:#636363">Lookup unavailable.</span>'; });
  }
  function positionDict() {
    var sel = window.getSelection();
    if (sel && sel.rangeCount) {
      var r = sel.getRangeAt(0).getBoundingClientRect();
      dictPopup.style.left = Math.min(r.left, window.innerWidth - 340) + 'px';
      dictPopup.style.top = Math.min(r.bottom + 8, window.innerHeight - 200) + 'px';
    }
  }
  function escapeHtml(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function applyDictionary() {
    if (prefs.dictionary) document.addEventListener('mouseup', onDictSelect);
    else { document.removeEventListener('mouseup', onDictSelect); if (dictPopup) dictPopup.style.display = 'none'; }
  }

  /* ================================================================
     INTERFACE — OVERSIZED (XL) WIDGET
     ================================================================ */
  function applyOversized() {
    if (!panel) return;
    if (prefs.oversized) panel.classList.add(PREFIX + 'xl');
    else panel.classList.remove(PREFIX + 'xl');
  }

  /* ================================================================
     WIDGET POSITION (existing)
     ================================================================ */
  function applyWidgetPosition() {
    var side = prefs.widgetPosition || 'right';
    var posCSS = side === 'left'
      ? 'bottom:24px !important;left:24px !important;right:auto !important;'
      : 'bottom:24px !important;right:24px !important;left:auto !important;';
    var panelPos = side === 'left' ? 'left:0;right:auto;' : 'right:0;left:auto;';
    var panelTransform = side === 'right' ? 'translateX(100%)' : 'translateX(-100%)';
    injectStyle('widget-position',
      '#' + WIDGET_ID + ' .' + PREFIX + 'trigger{' + posCSS + '}' +
      '.' + PREFIX + 'panel{' + panelPos + 'transform:' + panelTransform + ';}' +
      '.' + PREFIX + 'panel.aw-open{transform:translateX(0) !important;}' +
      '#' + PREFIX + 'speech-bar{' + (side === 'left' ? 'left:24px;right:auto;' : 'right:24px;left:auto;') + '}');
  }

  /* ================================================================
     LANGUAGE SWITCHING
     ================================================================ */
  function applyLanguage() {
    var meta = langMeta(prefs.language);
    /* Direction for the widget panel */
    if (panel) panel.setAttribute('dir', meta.dir);
    /* Inform assistive tech + client translation hook about the choice */
    try { document.documentElement.setAttribute('lang', prefs.language); } catch (e) {}
    if (meta.dir === 'rtl') document.documentElement.setAttribute('data-aw-dir', 'rtl');
    else document.documentElement.removeAttribute('data-aw-dir');
    if (typeof config.onLanguageChange === 'function') config.onLanguageChange(prefs.language, meta.dir);
  }

  /* ================================================================
     ACCESSIBILITY PROFILES (bundles of preferences)
     ================================================================ */
  var PROFILES = {
    seizureSafe:    { label: 'p_seizure',     desc: 'p_seizureDesc',     icon: '', set: { stopAnimations: true, saturation: 'low' } },
    visionImpaired: { label: 'p_vision',      desc: 'p_visionDesc',      icon: '', set: { contrastMode: 'high-contrast', textResize: 125, contentScale: 110, linkHighlight: true } },
    adhdFriendly:   { label: 'p_adhd',        desc: 'p_adhdDesc',        icon: '', set: { readingMask: true, highlightFocus: true, stopAnimations: true } },
    cognitive:      { label: 'p_cognitive',   desc: 'p_cognitiveDesc',   icon: '', set: { readableFont: true, textSpacing: 'comfortable', lineHeight: 'taller', readingGuide: true, linkHighlight: true } },
    keyboardNav:    { label: 'p_keyboard',    desc: 'p_keyboardDesc',    icon: '', set: { keyboardNav: true, highlightFocus: true } },
    screenReaderP:  { label: 'p_screenReader', desc: 'p_screenReaderDesc', icon: '', set: { screenReader: true } },
    olderAdults:    { label: 'p_older',       desc: 'p_olderDesc',       icon: '', set: { textResize: 125, contentScale: 110, cursorStyle: 'big-black', linkHighlight: true, contrastBoost: 1 } },
    colorBlind:     { label: 'p_colorBlind',  desc: 'p_colorBlindDesc',  icon: '', set: { colorBlindFilter: 'deuteranopia' } },
    dyslexia:       { label: 'p_dyslexia',    desc: 'p_dyslexiaDesc',    icon: '', set: { dyslexiaFont: true, textSpacing: 'comfortable', lineHeight: 'taller' } },
    motorImpaired:  { label: 'p_motor',       desc: 'p_motorDesc',       icon: '', set: { cursorStyle: 'big-black', oversized: true, keyboardNav: true } }
  };
  /* Order shown in the panel (mirrors the screenshots) */
  var PROFILE_ORDER = ['seizureSafe', 'visionImpaired', 'adhdFriendly', 'cognitive', 'keyboardNav', 'screenReaderP', 'olderAdults', 'colorBlind', 'dyslexia', 'motorImpaired'];

  function toggleProfile(id, on) {
    var p = PROFILES[id];
    if (!p) return;
    var idx = prefs.activeProfiles.indexOf(id);
    if (on) {
      if (idx === -1) prefs.activeProfiles.push(id);
      Object.keys(p.set).forEach(function (k) { prefs[k] = p.set[k]; });
    } else {
      if (idx > -1) prefs.activeProfiles.splice(idx, 1);
      Object.keys(p.set).forEach(function (k) { prefs[k] = defaults[k]; });
    }
    savePrefs();
    applyAll();
    renderControls();
    announceToggle(t(p.label), on);
  }

  /* ================================================================
     APPLY EVERYTHING
     ================================================================ */
  function applyAll() {
    applyLanguage();
    applyTextResize();
    applyContentScale();
    applyReadableFont();
    applyDyslexiaFont();
    applyTextSpacing();
    applyLineHeight();
    applyLetterSpacing();
    applyTextAlign();
    applyHighlightTitles();
    applyTextMagnifier();
    applyLinkHighlight();
    applyCursor();
    applyContrastMode();   /* also calls applyColorFilters */
    applyCustomColors();
    applyHideImages();
    applyMuteSounds();
    applyReadMode();
    applyReadingGuide();
    applyReadingMask();
    applyStopAnimations();
    applyHighlightHover();
    applyHighlightFocus();
    applyTooltips();
    applyKeyboardNav();
    applyScreenReader();
    applyVoiceNavigation();
    applyDictionary();
    applyWidgetPosition();
    applyOversized();
  }
  /* ================================================================
     PAGE TRANSLATION ENGINE
     Translates the host page's visible text and accessibility-relevant
     attributes into the language chosen in the panel, then reverts to the
     original text when the source language is selected again.

     • Pluggable: set config.translateText = (texts, from, to) => Promise<string[]>
       to use a licensed provider (Google Cloud Translation, DeepL, Azure …).
     • Default provider: a free, key-less, CORS-enabled endpoint
       (config.translationEndpoint). Machine-translation quality, rate-limited.
     • Privacy: with the default provider, visible page strings are sent to a
       third-party translation service. Use a self-hosted/licensed provider for
       sensitive sites.
     • Caching: translated strings are cached in-memory and in localStorage so
       repeat strings and return visits don't re-hit the network.
     ================================================================ */
  var TRANSLATABLE_ATTRS = ['placeholder', 'title', 'alt', 'aria-label', 'aria-placeholder', 'aria-roledescription'];
  var translationCache = null;        // { "from|to|text": "translated" }
  var appliedTranslations = [];       // restore closures for the current translation
  var transToast = null;
  var translateRunId = 0;
  var appliedLang = (config.sourceLanguage || 'en');   // language currently applied to the page

  function netReady() { return typeof Promise !== 'undefined' && (typeof config.translateText === 'function' || typeof fetch !== 'undefined'); }

  function loadTransCache() {
    if (translationCache) return;
    try { translationCache = JSON.parse(localStorage.getItem('aw-tcache') || '{}') || {}; }
    catch (e) { translationCache = {}; }
  }
  function saveTransCache() {
    try {
      var keys = Object.keys(translationCache);
      if (keys.length > 1500) { /* keep the most recent 1500 */
        var trimmed = {}; keys.slice(keys.length - 1500).forEach(function (k) { trimmed[k] = translationCache[k]; });
        translationCache = trimmed;
      }
      localStorage.setItem('aw-tcache', JSON.stringify(translationCache));
    } catch (e) { /* storage full / unavailable — non-fatal */ }
  }

  function transToastEl() {
    if (transToast) return transToast;
    transToast = document.createElement('div');
    transToast.className = PREFIX + 'trans-toast';
    transToast.setAttribute('role', 'status');
    transToast.setAttribute('aria-live', 'polite');
    (widgetRoot || document.documentElement).appendChild(transToast);
    return transToast;
  }
  function showTransLoading(on) {
    var el = transToastEl();
    if (on) {
      el.innerHTML = '<span class="' + PREFIX + 'trans-spin" aria-hidden="true"></span>' + escapeHtml(t('translating'));
      el.classList.add(PREFIX + 'show');
    } else {
      el.classList.remove(PREFIX + 'show');
    }
  }
  /* shared busy counter: the toast stays visible until BOTH the page pass and
     the widget-UI pass have finished (the widget translates a moment later). */
  var transBusy = 0;
  var _quotaHit = false;
  function beginTrans() { if (transBusy === 0) showTransLoading(true); transBusy++; }
  function endTrans() {
    transBusy = Math.max(0, transBusy - 1);
    if (transBusy === 0) {
      if (_quotaHit) { _quotaHit = false; showTransNotice(t('translateLimit')); }
      else showTransLoading(false);
    }
  }
  function showTransNotice(msg) {
    var el = transToastEl();
    el.innerHTML = escapeHtml(msg);
    el.classList.add(PREFIX + 'show');
    clearTimeout(el._noticeT);
    el._noticeT = setTimeout(function () { if (transBusy === 0) el.classList.remove(PREFIX + 'show'); }, 5000);
  }
  function anyUncached(texts, from, to) {
    loadTransCache();
    for (var i = 0; i < texts.length; i++) { if (translationCache[from + '|' + to + '|' + texts[i]] == null) return true; }
    return false;
  }

  function isTranslatableTextNode(node) {
    var v = node.nodeValue;
    if (!v || !v.trim() || v.trim().length < 1) return false;
    if (/^[\s\d\W]+$/.test(v.trim())) return false; /* skip pure numbers/punctuation/symbols */
    var p = node.parentNode;
    if (!p || p.nodeType !== 1) return false;
    if (p.closest('#' + WIDGET_ID)) return false;
    if (p.closest('script,style,noscript,code,pre,textarea,[translate="no"],.notranslate,[contenteditable="true"]')) return false;
    return true;
  }

  function collectUnits() {
    var units = [];
    if (!document.body) return units;
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) { return isTranslatableTextNode(n) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT; }
    });
    var n;
    while ((n = walker.nextNode())) {
      (function (node) {
        var original = node.nodeValue;
        var trimmed = original.trim();
        units.push({
          text: trimmed,
          set: function (tr) { node.nodeValue = original.replace(trimmed, tr); },
          restore: function () { node.nodeValue = original; }
        });
      })(n);
    }
    var sel = TRANSLATABLE_ATTRS.map(function (a) { return '[' + a + ']'; }).join(',');
    Array.prototype.forEach.call(document.body.querySelectorAll(sel), function (elm) {
      if (elm.closest('#' + WIDGET_ID)) return;
      if (elm.closest('[translate="no"],.notranslate')) return;
      TRANSLATABLE_ATTRS.forEach(function (a) {
        var v = elm.getAttribute(a);
        if (!v || !v.trim()) return;
        (function (el, attr, orig) {
          units.push({
            text: orig.trim(),
            set: function (tr) { el.setAttribute(attr, tr); },
            restore: function () { el.setAttribute(attr, orig); }
          });
        })(elm, a, v);
      });
    });
    return units;
  }

  function revertTranslation() {
    for (var i = appliedTranslations.length - 1; i >= 0; i--) {
      try { appliedTranslations[i](); } catch (e) {}
    }
    appliedTranslations = [];
  }

  /* concurrency-limited promise runner */
  function runLimited(items, limit, worker) {
    return new Promise(function (resolve) {
      var i = 0, done = 0, total = items.length, active = 0;
      if (!total) return resolve();
      function pump() {
        while (active < limit && i < total) {
          var it = items[i++]; active++;
          Promise.resolve(worker(it)).then(fin, fin);
        }
      }
      function fin() { active--; done++; (done >= total) ? resolve() : pump(); }
      pump();
    });
  }

  function defaultProvider(texts, from, to) {
    var text = texts[0];
    var url = (config.translationEndpoint || '')
      .replace('{text}', encodeURIComponent(text))
      .replace('{from}', encodeURIComponent(from))
      .replace('{to}', encodeURIComponent(to));
    if (!url) return Promise.resolve([null]);
    /* A contact email raises MyMemory's free daily allowance (~1k → ~50k words)
       and cuts rate-limit errors. Set config.translationEmail to enable. */
    if (config.translationEmail && url.indexOf('mymemory') > -1) url += '&de=' + encodeURIComponent(config.translationEmail);
    return fetch(url).then(function (r) { return r.json(); }).then(function (d) {
      var status = d && d.responseStatus;
      var tr = d && d.responseData && d.responseData.translatedText;
      var bad = !tr || /MYMEMORY WARNING|QUERY LENGTH LIMIT|INVALID|USED ALL|EXCEED/i.test(tr) ||
                (status && status !== 200 && status !== '200');
      if (tr && /MYMEMORY WARNING|QUERY LENGTH LIMIT|USED ALL|EXCEED/i.test(tr)) _quotaHit = true;
      if (status === 403 || status === '403' || status === 429 || status === '429') _quotaHit = true;
      /* Return null on failure so it is NOT cached and will be retried later,
         instead of poisoning the cache with the original (untranslated) text. */
      return [bad ? null : tr];
    }, function () { return [null]; });
  }

  var _entityDecoder = null;
  function decodeEntities(str) {
    if (str == null || String(str).indexOf('&') === -1) return str;
    try {
      if (!_entityDecoder) _entityDecoder = document.createElement('textarea');
      _entityDecoder.innerHTML = String(str);
      return _entityDecoder.value;
    } catch (e) { return str; }
  }

  function translateTexts(texts, from, to) {
    loadTransCache();
    var map = {}, pending = [];
    texts.forEach(function (txt) {
      var key = from + '|' + to + '|' + txt;
      if (translationCache[key] != null) map[txt] = translationCache[key];
      else pending.push(txt);
    });
    if (!pending.length) return Promise.resolve(map);
    var provider = (typeof config.translateText === 'function') ? config.translateText : defaultProvider;
    return runLimited(pending, 4, function (txt) {
      return Promise.resolve(provider([txt], from, to)).then(function (res) {
        var tr = Array.isArray(res) ? res[0] : res;
        if (tr != null && tr !== '') {
          tr = decodeEntities(tr);                 /* providers often HTML-encode (&#10; &#39; &amp;) */
          map[txt] = tr; translationCache[from + '|' + to + '|' + txt] = tr;
        }
      }, function () { /* leave untranslated on failure */ });
    }).then(function () { return map; });
  }

  /* Translate the widget's OWN interface for languages that aren't fully
     localized (or for keys missing from a locale). It only touches text still in
     the source language, so hand-written LOCALE translations are preserved. */
  var _englishSet = null;
  function widgetEnglishValues() {
    if (_englishSet) return _englishSet;
    _englishSet = {};
    for (var k in STRINGS) { if (STRINGS.hasOwnProperty(k) && typeof STRINGS[k] === 'string') { var v = STRINGS[k].trim(); if (v) _englishSet[v] = 1; } }
    /* config-provided UI text is authored in the source language too, so it
       should translate like the rest of the widget (e.g. Useful-links labels) */
    if (config.usefulLinks) config.usefulLinks.forEach(function (l) { if (l && l.label) { var v = String(l.label).trim(); if (v) _englishSet[v] = 1; } });
    if (config.labels) ['panelTitle', 'resetButton', 'closeButton'].forEach(function (key) { var v = config.labels[key]; if (v) { v = String(v).trim(); if (v) _englishSet[v] = 1; } });
    return _englishSet;
  }
  function translateWidgetUI(target) {
    var source = config.sourceLanguage || 'en';
    if (!config.pageTranslation || !target || target === source) return;
    if (!widgetRoot || !netReady()) return;
    var eng = widgetEnglishValues();
    var units = [];
    var w = document.createTreeWalker(widgetRoot, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        var tv = n.nodeValue; if (!tv || !tv.trim()) return NodeFilter.FILTER_REJECT;
        return eng[tv.trim()] ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    var n;
    while ((n = w.nextNode())) { (function (node) { var orig = node.nodeValue, trimmed = orig.trim(); units.push({ text: trimmed, set: function (tr) { node.nodeValue = orig.replace(trimmed, tr); } }); })(n); }
    ['placeholder', 'aria-label'].forEach(function (attr) {
      Array.prototype.forEach.call(widgetRoot.querySelectorAll('[' + attr + ']'), function (elm) {
        var v = elm.getAttribute(attr);
        if (v && eng[v.trim()]) (function (e, a, o) { units.push({ text: o.trim(), set: function (tr) { e.setAttribute(a, tr); } }); })(elm, attr, v);
      });
    });
    if (!units.length) return;
    var groups = {}; units.forEach(function (u) { (groups[u.text] = groups[u.text] || []).push(u); });
    var texts = Object.keys(groups);
    var busy = anyUncached(texts, source, target);
    if (busy) beginTrans();
    translateTexts(texts, source, target).then(function (map) {
      if ((prefs.language || source) === target) {   /* still the active language */
        texts.forEach(function (txt) { var tr = map[txt]; if (tr == null || tr === txt) return; groups[txt].forEach(function (u) { try { u.set(tr); } catch (e) {} }); });
        saveTransCache();
        /* Some strings may have failed (e.g. provider rate-limit) and were left
           uncached — retry them a few times so the UI fills in once quota frees. */
        var missing = texts.some(function (t) { return translationCache[source + '|' + target + '|' + t] == null; });
        if (missing) {
          _wuiRetries[target] = (_wuiRetries[target] || 0) + 1;
          if (_wuiRetries[target] <= 4) setTimeout(function () { if ((prefs.language || source) === target) translateWidgetUI(target); }, 2500 * _wuiRetries[target]);
        } else { _wuiRetries[target] = 0; }
      }
      if (busy) endTrans();
    }, function () { if (busy) endTrans(); });
  }
  var _wuiRetries = {};
  /* coalesce the rapid re-renders around a language switch into one widget pass */
  var _wuiTimer = null, _wuiLang = null;
  function scheduleWidgetUI(target) {
    _wuiLang = target;
    if (_wuiTimer) return;
    _wuiTimer = setTimeout(function () { _wuiTimer = null; translateWidgetUI(_wuiLang); }, 50);
  }

  function translatePage(targetLang, force) {
    var source = config.sourceLanguage || 'en';
    if (!force && targetLang === appliedLang) return; /* already in this state */
    /* Always revert any prior translation before applying a new one */
    revertTranslation();
    appliedLang = targetLang;
    var myRun = ++translateRunId;
    if (!config.pageTranslation || !targetLang || targetLang === source) { return; }
    if (!netReady()) { /* environment can't fetch and no custom provider */ return; }

    var units = collectUnits();
    if (!units.length) return;

    /* group identical strings so each unique string is translated once */
    var groups = {};
    units.forEach(function (u) { if (u.text) (groups[u.text] = groups[u.text] || []).push(u); });
    var texts = Object.keys(groups);
    var cap = config.translationMaxUnits || 800;
    if (texts.length > cap) texts = texts.slice(0, cap);

    var busy = anyUncached(texts, source, targetLang);
    if (busy) beginTrans();
    translateTexts(texts, source, targetLang).then(function (map) {
      if (myRun === translateRunId) {
        texts.forEach(function (txt) {
          var tr = map[txt];
          if (tr == null || tr === txt) return;
          groups[txt].forEach(function (u) { appliedTranslations.push(u.restore); try { u.set(tr); } catch (e) {} });
        });
        saveTransCache();
      }
      if (busy) endTrans();
    }, function () { if (busy) endTrans(); });
  }
  /* ================================================================
     WIDGET CHROME STYLES  (v2 redesign — "Control Center")
     A single injected stylesheet, fully themed via CSS custom properties
     so the whole UI re-skins from config colors and adapts to dark mode.
     Scoped under #aw-widget-root; the widget lives outside <body> so the
     page-wide filters never touch it.
     ================================================================ */
  function injectWidgetStyles() {
    var c   = config.primaryColor;
    var ac  = config.accentColor;
    var z   = config.zIndex;
    var sz  = config.buttonSize;

    var vars =
    '#' + WIDGET_ID + '{' +
      '--aw-accent:' + c + ';' +
      '--aw-accent-press:' + shade(c, -14) + ';' +
      '--aw-accent-soft:' + tint(c, 0.88) + ';' +
      '--aw-accent-line:' + tint(c, 0.62) + ';' +
      '--aw-focus:' + ac + ';' +
      '--aw-bg:#eceef3;' +
      '--aw-surface:#ffffff;' +
      '--aw-surface-2:#f1f3f7;' +
      '--aw-surface-3:#e9ecf2;' +
      '--aw-ink:#171b22;' +
      '--aw-ink-2:#3b424d;' +
      '--aw-muted:#69727f;' +
      '--aw-hair:#e6e8ef;' +
      '--aw-hair-2:#dfe2ea;' + '--aw-track:#c4cbd6;' +
      '--aw-shadow:0 18px 50px -12px rgba(20,26,38,.32);' +
      '--aw-card-shadow:0 1px 2px rgba(20,26,38,.04),0 8px 24px -16px rgba(20,26,38,.18);' +
      '--aw-r-lg:26px;--aw-r-md:18px;--aw-r-sm:13px;' +
      'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;' +
    '}' +
    /* dark mode */
    '@media (prefers-color-scheme:dark){#' + WIDGET_ID + '{' +
      '--aw-accent-soft:' + shade(c, 38) + '33;' +
      '--aw-accent-line:' + shade(c, 24) + ';' +
      '--aw-bg:#0e1116;' +
      '--aw-surface:#171b22;' +
      '--aw-surface-2:#1d222b;' +
      '--aw-surface-3:#222834;' +
      '--aw-ink:#eef1f6;' +
      '--aw-ink-2:#c2c8d2;' +
      '--aw-muted:#98a2b2;' +
      '--aw-hair:#272d38;' +
      '--aw-hair-2:#2f3744;' + '--aw-track:#3a4250;' +
      '--aw-card-shadow:0 1px 2px rgba(0,0,0,.4),0 10px 30px -18px rgba(0,0,0,.7);' +
    '}}';

    var css = vars +
    '#' + WIDGET_ID + '{all:initial;}' +
    '#' + WIDGET_ID + '{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;line-height:1.5;}' +
    ':where(#' + WIDGET_ID + ' *,#' + WIDGET_ID + ' *::before,#' + WIDGET_ID + ' *::after){box-sizing:border-box;margin:0;padding:0;font-family:inherit;line-height:inherit;}' +
    ':where(#' + WIDGET_ID + ' button){font:inherit;color:inherit;background:none;border:none;cursor:pointer;line-height:inherit;}' +
    ':where(#' + WIDGET_ID + ' svg){display:block;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round;}' +

    /* ---------- trigger FAB ---------- */
    '.' + PREFIX + 'trigger{position:fixed;bottom:max(22px,env(safe-area-inset-bottom));right:22px;width:' + sz + 'px;height:' + sz + 'px;border-radius:50%;background:var(--aw-accent);color:#fff;z-index:' + z + ';display:flex;align-items:center;justify-content:center;box-shadow:0 6px 16px rgba(20,26,38,.28),0 2px 5px rgba(20,26,38,.18);transition:transform .18s cubic-bezier(.2,.7,.2,1),box-shadow .18s ease;}' +
    '.' + PREFIX + 'trigger:hover{transform:translateY(-2px) scale(1.05);box-shadow:0 12px 26px rgba(20,26,38,.32);}' +
    '.' + PREFIX + 'trigger:active{transform:scale(.96);}' +
    '.' + PREFIX + 'trigger:focus-visible{outline:3px solid var(--aw-focus);outline-offset:3px;}' +
    '.' + PREFIX + 'trigger svg{width:56%;height:56%;stroke:#fff;}' +

    /* ---------- backdrop ---------- */
    '.' + PREFIX + 'backdrop{position:fixed;inset:0;background:rgba(15,18,24,.42);z-index:' + (z + 1) + ';opacity:0;visibility:hidden;transition:opacity .25s ease,visibility .25s ease;backdrop-filter:saturate(1.1) blur(2px);-webkit-backdrop-filter:saturate(1.1) blur(2px);}' +
    '.' + PREFIX + 'backdrop.' + PREFIX + 'open{opacity:1;visibility:visible;}' +

    /* ---------- panel shell ---------- */
    '.' + PREFIX + 'panel{position:fixed;top:0;right:0;height:100%;width:404px;max-width:100vw;background:var(--aw-bg);color:var(--aw-ink);z-index:' + (z + 2) + ';box-shadow:var(--aw-shadow);display:flex;flex-direction:column;overflow:hidden;transform:translateX(102%);transition:transform .34s cubic-bezier(.22,.61,.36,1);border-top-left-radius:var(--aw-r-lg);border-bottom-left-radius:var(--aw-r-lg);will-change:transform;}' +
    '.' + PREFIX + 'panel.' + PREFIX + 'open{transform:translateX(0);}' +
    '.' + PREFIX + 'panel.' + PREFIX + 'xl{width:476px;font-size:17px;}' +
    '.' + PREFIX + 'grip{display:none;}' +

    /* ---------- header ---------- */
    '.' + PREFIX + 'header{flex:0 0 auto;display:flex;align-items:center;gap:12px;padding:18px 18px 14px;background:var(--aw-surface);border-bottom:1px solid var(--aw-hair);}' +
    '.' + PREFIX + 'brand{width:38px;height:38px;border-radius:12px;background:var(--aw-accent);color:#fff;display:flex;align-items:center;justify-content:center;flex:0 0 auto;box-shadow:0 4px 12px -4px var(--aw-accent);}' +
    '.' + PREFIX + 'brand svg{width:22px;height:22px;stroke:#fff;}' +
    '.' + PREFIX + 'title{font-size:17px;font-weight:750;letter-spacing:-.015em;line-height:1.1;color:var(--aw-ink);display:flex;flex-direction:column;gap:1px;flex:1 1 auto;min-width:0;}' +
    '.' + PREFIX + 'title small{font-size:11.5px;font-weight:550;letter-spacing:0;color:var(--aw-muted);}' +
    '.' + PREFIX + 'panel.' + PREFIX + 'xl .' + PREFIX + 'title{font-size:20px;}' +
    '.' + PREFIX + 'close{width:38px;height:38px;border-radius:50%;background:var(--aw-surface-3);color:var(--aw-ink-2);display:flex;align-items:center;justify-content:center;flex:0 0 auto;transition:background .15s ease,transform .12s ease;}' +
    '.' + PREFIX + 'close:hover{background:var(--aw-hair-2);}' +
    '.' + PREFIX + 'close:active{transform:scale(.92);}' +
    '.' + PREFIX + 'close:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +
    '.' + PREFIX + 'close svg{width:17px;height:17px;stroke:currentColor;stroke-width:2.4;fill:none;}' +

    /* ---------- sticky sub-header: search + chips ---------- */
    '.' + PREFIX + 'subhead{flex:0 0 auto;background:var(--aw-surface);padding:0 14px 12px;border-bottom:1px solid var(--aw-hair);}' +
    '.' + PREFIX + 'search{position:relative;margin-bottom:10px;}' +
    '.' + PREFIX + 'search svg{position:absolute;left:13px;top:50%;transform:translateY(-50%);width:17px;height:17px;stroke:var(--aw-muted);stroke-width:2;fill:none;pointer-events:none;}' +
    '.' + PREFIX + 'search input{width:100%;height:42px;padding:0 38px 0 38px;border-radius:12px;border:1.5px solid var(--aw-hair-2);background:var(--aw-surface-2);color:var(--aw-ink);font-size:14.5px;}' +
    '.' + PREFIX + 'search input::placeholder{color:var(--aw-muted);}' +
    '.' + PREFIX + 'search input:focus{outline:none;border-color:var(--aw-accent);background:var(--aw-surface);}' +
    '.' + PREFIX + 'search-clear{position:absolute;right:8px;top:50%;transform:translateY(-50%);width:26px;height:26px;border-radius:50%;display:none;align-items:center;justify-content:center;color:var(--aw-muted);background:var(--aw-surface-3);}' +
    '.' + PREFIX + 'search-clear.' + PREFIX + 'show{display:flex;}' +
    '.' + PREFIX + 'search-clear svg{width:13px;height:13px;stroke:currentColor;stroke-width:2.4;fill:none;}' +
    '.' + PREFIX + 'chips{display:flex;gap:7px;overflow-x:auto;scrollbar-width:none;-ms-overflow-style:none;padding-bottom:2px;scroll-behavior:smooth;cursor:grab;}' +
    '.' + PREFIX + 'chips.' + PREFIX + 'dragging{cursor:grabbing;scroll-behavior:auto;user-select:none;}' +
    '.' + PREFIX + 'chips.' + PREFIX + 'dragging .' + PREFIX + 'chip{pointer-events:none;}' +
    '.' + PREFIX + 'chips::-webkit-scrollbar{display:none;}' +
    '.' + PREFIX + 'chip{flex:0 0 auto;display:inline-flex;align-items:center;gap:6px;height:34px;padding:0 13px;border-radius:999px;background:var(--aw-surface-3);border:1px solid var(--aw-hair-2);color:var(--aw-ink-2);font-size:13px;font-weight:600;white-space:nowrap;transition:background .14s ease,color .14s ease,transform .1s ease;}' +
    '.' + PREFIX + 'chip svg{width:15px;height:15px;stroke:currentColor;stroke-width:1.9;fill:none;}' +
    '.' + PREFIX + 'chip:hover{background:var(--aw-hair-2);}' +
    '.' + PREFIX + 'chip:active{transform:scale(.95);}' +
    '.' + PREFIX + 'chip.' + PREFIX + 'cur{background:var(--aw-accent);border-color:var(--aw-accent);color:#fff;}' +
    '.' + PREFIX + 'chip:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +

    /* ---------- body ---------- */
    '.' + PREFIX + 'body{position:relative;flex:1 1 auto;overflow-y:auto;overflow-x:hidden;padding:14px;-webkit-overflow-scrolling:touch;scroll-behavior:smooth;}' +
    '.' + PREFIX + 'body::-webkit-scrollbar{width:9px;}' +
    '.' + PREFIX + 'body::-webkit-scrollbar-thumb{background:var(--aw-hair-2);border-radius:6px;border:2px solid var(--aw-bg);}' +

    /* ---------- section ---------- */
    '.' + PREFIX + 'section{background:var(--aw-surface);border-radius:var(--aw-r-md);padding:15px 14px 16px;margin-bottom:13px;box-shadow:var(--aw-card-shadow);scroll-margin-top:8px;}' +
    '.' + PREFIX + 'sec-head{display:flex;align-items:center;gap:9px;margin-bottom:13px;}' +
    '.' + PREFIX + 'sec-ic{width:26px;height:26px;border-radius:8px;background:var(--aw-accent-soft);display:none;align-items:center;justify-content:center;flex:0 0 auto;}' +
    '.' + PREFIX + 'sec-ic svg{width:15px;height:15px;stroke:var(--aw-accent);stroke-width:1.9;fill:none;}' +
    '.' + PREFIX + 'section-title{font-size:11.5px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:var(--aw-muted);}' +
    '.' + PREFIX + 'section.' + PREFIX + 'prominent .' + PREFIX + 'sec-ic{display:flex;}' +
    '.' + PREFIX + 'section.' + PREFIX + 'prominent .' + PREFIX + 'section-title{font-size:15px;font-weight:750;text-transform:none;letter-spacing:-.01em;color:var(--aw-ink);}' +
    '.' + PREFIX + 'sec-sub{font-size:12.5px;color:var(--aw-muted);margin:-4px 0 12px;line-height:1.45;}' +
    '.' + PREFIX + 'panel.' + PREFIX + 'xl .' + PREFIX + 'section-title{font-size:13px;}' +
    '.' + PREFIX + 'panel.' + PREFIX + 'xl .' + PREFIX + 'section.' + PREFIX + 'prominent .' + PREFIX + 'section-title{font-size:17px;}' +

    /* ---------- bento tile grid ---------- */
    '.' + PREFIX + 'grid{display:grid;grid-template-columns:repeat(3,1fr);gap:9px;}' +
    '.' + PREFIX + 'tile{position:relative;background:var(--aw-surface-2);border:1.5px solid var(--aw-hair);border-radius:var(--aw-r-sm);padding:13px 7px 11px;display:flex;flex-direction:column;align-items:center;justify-content:flex-start;gap:8px;text-align:center;min-height:92px;color:var(--aw-ink-2);transition:background .15s ease,border-color .15s ease,transform .1s ease,color .15s ease;}' +
    '.' + PREFIX + 'tile:hover{background:var(--aw-surface-3);}' +
    '.' + PREFIX + 'tile:active{transform:scale(.96);}' +
    '.' + PREFIX + 'tile:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +
    '.' + PREFIX + 'tile.' + PREFIX + 'active{background:var(--aw-accent-soft);border-color:var(--aw-accent-line);color:var(--aw-accent);}' +
    '.' + PREFIX + 'tile .' + PREFIX + 'ic{width:30px;height:30px;border-radius:9px;display:flex;align-items:center;justify-content:center;background:var(--aw-surface);box-shadow:0 1px 2px rgba(20,26,38,.06);transition:background .15s ease;}' +
    '.' + PREFIX + 'tile.' + PREFIX + 'active .' + PREFIX + 'ic{background:#fff;}' +
    '.' + PREFIX + 'tile .' + PREFIX + 'ic svg{width:19px;height:19px;stroke:var(--aw-ink-2);stroke-width:1.8;fill:none;}' +
    '.' + PREFIX + 'tile.' + PREFIX + 'active .' + PREFIX + 'ic svg{stroke:var(--aw-accent);}' +
    '.' + PREFIX + 'tile .' + PREFIX + 'lab{font-size:12px;font-weight:600;line-height:1.25;letter-spacing:-.005em;}' +
    '.' + PREFIX + 'tile .' + PREFIX + 'dot{position:absolute;top:8px;right:8px;width:7px;height:7px;border-radius:50%;background:var(--aw-accent);opacity:0;transform:scale(.4);transition:opacity .15s ease,transform .15s ease;}' +
    '.' + PREFIX + 'tile.' + PREFIX + 'active .' + PREFIX + 'dot{opacity:1;transform:scale(1);}' +
    '.' + PREFIX + 'span2{grid-column:span 2;}' +
    '.' + PREFIX + 'cap{font-size:10.5px;font-weight:700;color:var(--aw-muted);margin-top:-3px;letter-spacing:-.01em;}' +
    '.' + PREFIX + 'tile.' + PREFIX + 'active .' + PREFIX + 'cap{color:var(--aw-accent);}' +
    '.' + PREFIX + 'pips{display:flex;gap:4px;margin-top:5px;}' +
    '.' + PREFIX + 'pip{width:5px;height:5px;border-radius:50%;background:var(--aw-hair-2);transition:background .15s ease;}' +
    '.' + PREFIX + 'pip-on{background:var(--aw-accent);}' +
    '.' + PREFIX + 'span3{grid-column:1 / -1;}' +
    '.' + PREFIX + 'panel.' + PREFIX + 'xl .' + PREFIX + 'tile{min-height:108px;padding:16px 9px;}' +
    '.' + PREFIX + 'panel.' + PREFIX + 'xl .' + PREFIX + 'tile .' + PREFIX + 'lab{font-size:14px;}' +
    '.' + PREFIX + 'panel.' + PREFIX + 'xl .' + PREFIX + 'tile .' + PREFIX + 'ic{width:38px;height:38px;}' +
    '.' + PREFIX + 'panel.' + PREFIX + 'xl .' + PREFIX + 'tile .' + PREFIX + 'ic svg{width:23px;height:23px;}' +

    /* ---------- stepper ---------- */
    '.' + PREFIX + 'stepper-wrap{background:var(--aw-surface-2);border-radius:var(--aw-r-sm);padding:11px 12px;margin-bottom:9px;}' +
    '.' + PREFIX + 'stepper-head{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:var(--aw-ink-2);margin-bottom:9px;}' +
    '.' + PREFIX + 'stepper-head .' + PREFIX + 'ic{width:24px;height:24px;border-radius:7px;background:var(--aw-surface);display:flex;align-items:center;justify-content:center;flex:0 0 auto;}' +
    '.' + PREFIX + 'stepper-head .' + PREFIX + 'ic svg{width:15px;height:15px;stroke:var(--aw-ink-2);stroke-width:1.8;fill:none;}' +
    '.' + PREFIX + 'stepper{display:flex;align-items:center;gap:8px;}' +
    '.' + PREFIX + 'stepper button{width:40px;height:40px;border-radius:11px;background:var(--aw-surface);color:var(--aw-accent);font-size:21px;font-weight:600;display:flex;align-items:center;justify-content:center;flex:0 0 auto;box-shadow:0 1px 2px rgba(20,26,38,.08);transition:transform .1s ease,background .14s ease;}' +
    '.' + PREFIX + 'stepper button:hover{background:var(--aw-surface-3);}' +
    '.' + PREFIX + 'stepper button:active{transform:scale(.9);}' +
    '.' + PREFIX + 'stepper button:disabled{color:var(--aw-muted);opacity:.45;cursor:not-allowed;}' +
    '.' + PREFIX + 'stepper button:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +
    '.' + PREFIX + 'stepper .' + PREFIX + 'val{flex:1 1 auto;text-align:center;font-size:14px;font-weight:700;color:var(--aw-ink);letter-spacing:-.01em;}' +

    /* ---------- profile rows ---------- */
    '.' + PREFIX + 'prow{display:flex;align-items:center;gap:12px;padding:11px 2px;border-top:1px solid var(--aw-hair);}' +
    '.' + PREFIX + 'prow:first-of-type{border-top:none;}' +
    '.' + PREFIX + 'picon{width:36px;height:36px;border-radius:10px;background:var(--aw-surface-2);display:flex;align-items:center;justify-content:center;flex:0 0 auto;}' +
    '.' + PREFIX + 'picon svg{width:19px;height:19px;stroke:var(--aw-ink-2);stroke-width:1.8;fill:none;}' +
    '.' + PREFIX + 'prow.' + PREFIX + 'pon .' + PREFIX + 'picon{background:var(--aw-accent-soft);}' +
    '.' + PREFIX + 'prow.' + PREFIX + 'pon .' + PREFIX + 'picon svg{stroke:var(--aw-accent);}' +
    '.' + PREFIX + 'pmeta{flex:1 1 auto;min-width:0;}' +
    '.' + PREFIX + 'pname{font-size:14px;font-weight:650;color:var(--aw-ink);letter-spacing:-.01em;}' +
    '.' + PREFIX + 'pdesc{font-size:12px;color:var(--aw-muted);margin-top:1px;line-height:1.35;}' +
    /* iOS-style switch (single tap target, role=switch) */
    '.' + PREFIX + 'seg{position:relative;flex:0 0 auto;width:50px;height:30px;border-radius:999px;background:var(--aw-track);transition:background .2s ease;padding:0;}' +
    '.' + PREFIX + 'seg .' + PREFIX + 'knob{position:absolute;top:3px;left:3px;width:24px;height:24px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(20,26,38,.35);transition:transform .2s cubic-bezier(.4,1.2,.5,1);}' +
    '.' + PREFIX + 'seg.' + PREFIX + 'on{background:var(--aw-accent);}' +
    '.' + PREFIX + 'seg.' + PREFIX + 'on .' + PREFIX + 'knob{transform:translateX(20px);}' +
    '.' + PREFIX + 'seg:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +

    /* ---------- big toggle row (oversized) ---------- */
    '.' + PREFIX + 'xl-row{display:flex;align-items:center;gap:13px;}' +
    '.' + PREFIX + 'xl-badge{width:42px;height:42px;border-radius:12px;background:var(--aw-accent-soft);color:var(--aw-accent);font-weight:800;font-size:15px;letter-spacing:-.02em;display:flex;align-items:center;justify-content:center;flex:0 0 auto;}' +
    '.' + PREFIX + 'xl-lab{font-size:14px;font-weight:650;color:var(--aw-ink);}' +
    '.' + PREFIX + 'xl-sub{font-size:12px;color:var(--aw-muted);margin-top:1px;}' +
    '.' + PREFIX + 'switch{margin-left:auto;position:relative;width:50px;height:30px;flex:0 0 auto;}' +
    '.' + PREFIX + 'switch input{position:absolute;inset:0;opacity:0;width:100%;height:100%;margin:0;cursor:pointer;}' +
    '.' + PREFIX + 'switch .' + PREFIX + 'slider{position:absolute;inset:0;background:var(--aw-track);border-radius:999px;transition:background .2s ease;}' +
    '.' + PREFIX + 'switch .' + PREFIX + 'slider::before{content:"";position:absolute;width:24px;height:24px;left:3px;top:3px;background:#fff;border-radius:50%;box-shadow:0 1px 3px rgba(20,26,38,.35);transition:transform .2s cubic-bezier(.4,1.2,.5,1);}' +
    '.' + PREFIX + 'switch input:checked + .' + PREFIX + 'slider{background:var(--aw-accent);}' +
    '.' + PREFIX + 'switch input:checked + .' + PREFIX + 'slider::before{transform:translateX(20px);}' +
    '.' + PREFIX + 'switch input:focus-visible + .' + PREFIX + 'slider{outline:3px solid var(--aw-focus);outline-offset:2px;}' +

    /* ---------- swatch picker ---------- */
    '.' + PREFIX + 'swatch-card{background:var(--aw-surface-2);border-radius:var(--aw-r-sm);padding:12px;margin-bottom:9px;}' +
    '.' + PREFIX + 'sw-title{font-size:12.5px;font-weight:600;color:var(--aw-ink-2);margin-bottom:10px;display:flex;align-items:center;justify-content:space-between;}' +
    '.' + PREFIX + 'swatches{display:flex;flex-wrap:wrap;gap:9px;}' +
    '.' + PREFIX + 'sw{width:28px;height:28px;border-radius:50%;border:2px solid rgba(0,0,0,.08);position:relative;transition:transform .12s ease;}' +
    '.' + PREFIX + 'sw:hover{transform:scale(1.12);}' +
    '.' + PREFIX + 'sw:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +
    '.' + PREFIX + 'sw.' + PREFIX + 'sel{box-shadow:0 0 0 2px var(--aw-surface-2),0 0 0 4px var(--aw-accent);}' +
    '.' + PREFIX + 'sw-clear{margin-left:auto;color:var(--aw-accent);font-size:12px;font-weight:650;padding:2px 4px;}' +
    '.' + PREFIX + 'sw-clear:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;border-radius:6px;}' +

    /* ---------- useful links ---------- */
    '.' + PREFIX + 'links{display:flex;flex-direction:column;max-height:300px;overflow-y:auto;border-radius:var(--aw-r-sm);background:var(--aw-surface-2);}' +
    '.' + PREFIX + 'links::-webkit-scrollbar{width:8px;}' +
    '.' + PREFIX + 'links::-webkit-scrollbar-thumb{background:var(--aw-hair-2);border-radius:6px;}' +
    '.' + PREFIX + 'link-row{display:flex;align-items:center;gap:12px;padding:13px 13px;border-top:1px solid var(--aw-hair);text-align:left;width:100%;transition:background .14s ease;}' +
    '.' + PREFIX + 'link-row:first-child{border-top:none;}' +
    '.' + PREFIX + 'link-row:hover{background:var(--aw-surface-3);}' +
    '.' + PREFIX + 'link-row:active{background:var(--aw-hair-2);}' +
    '.' + PREFIX + 'link-row:focus-visible{outline:3px solid var(--aw-focus);outline-offset:-3px;border-radius:var(--aw-r-sm);}' +
    '.' + PREFIX + 'link-ic{width:32px;height:32px;border-radius:9px;background:var(--aw-accent-soft);display:flex;align-items:center;justify-content:center;flex:0 0 auto;}' +
    '.' + PREFIX + 'link-ic svg{width:16px;height:16px;stroke:var(--aw-accent);stroke-width:1.9;fill:none;}' +
    '.' + PREFIX + 'link-label{flex:1 1 auto;font-size:14px;font-weight:600;color:var(--aw-ink);min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}' +
    '.' + PREFIX + 'link-go{flex:0 0 auto;color:var(--aw-muted);}' +
    '.' + PREFIX + 'link-go svg{width:16px;height:16px;stroke:currentColor;stroke-width:2;fill:none;}' +

    /* ---------- footer ---------- */
    '.' + PREFIX + 'footer{flex:0 0 auto;padding:12px 14px max(14px,env(safe-area-inset-bottom));background:var(--aw-surface);border-top:1px solid var(--aw-hair);}' +
    '.' + PREFIX + 'reset{width:100%;height:46px;border-radius:13px;background:var(--aw-surface-3);color:var(--aw-ink);font-size:14px;font-weight:700;display:flex;align-items:center;justify-content:center;gap:8px;transition:background .14s ease,transform .1s ease;}' +
    '.' + PREFIX + 'reset:hover{background:var(--aw-hair-2);}' +
    '.' + PREFIX + 'reset:active{transform:scale(.99);}' +
    '.' + PREFIX + 'reset:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +
    '.' + PREFIX + 'reset svg{width:16px;height:16px;stroke:currentColor;stroke-width:2;fill:none;}' +
    '.' + PREFIX + 'disclaimer{font-size:10.5px;color:var(--aw-muted);text-align:center;margin-top:9px;line-height:1.45;}' +
    '.' + PREFIX + 'hint{font-size:11px;color:var(--aw-muted);text-align:center;margin-top:6px;opacity:.85;}' +

    /* ---------- empty (search no-results) ---------- */
    '.' + PREFIX + 'empty{display:none;text-align:center;padding:40px 16px;color:var(--aw-muted);}' +
    '.' + PREFIX + 'empty.' + PREFIX + 'show{display:block;}' +
    '.' + PREFIX + 'empty svg{width:34px;height:34px;stroke:var(--aw-hair-2);stroke-width:1.6;fill:none;margin:0 auto 10px;}' +
    '.' + PREFIX + 'empty p{font-size:13.5px;}' +

    /* ---------- language selector ---------- */
    '.' + PREFIX + 'lang-btn{width:100%;display:flex;align-items:center;gap:11px;background:var(--aw-surface-2);border-radius:var(--aw-r-sm);padding:11px 13px;font-size:14px;font-weight:600;color:var(--aw-ink);transition:background .14s ease;}' +
    '.' + PREFIX + 'lang-btn:hover{background:var(--aw-surface-3);}' +
    '.' + PREFIX + 'lang-btn:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +
    '.' + PREFIX + 'flag{width:30px;height:30px;border-radius:9px;background:var(--aw-accent);color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex:0 0 auto;text-transform:uppercase;letter-spacing:.02em;}' +
    '.' + PREFIX + 'caret{margin-left:auto;color:var(--aw-muted);transition:transform .2s ease;}' +
    '.' + PREFIX + 'caret svg{width:16px;height:16px;stroke:currentColor;stroke-width:2;fill:none;}' +
    '.' + PREFIX + 'lang-btn[aria-expanded="true"] .' + PREFIX + 'caret{transform:rotate(180deg);}' +
    '.' + PREFIX + 'lang-pop{margin-top:10px;display:none;}' +
    '.' + PREFIX + 'lang-pop.' + PREFIX + 'open{display:block;}' +
    '.' + PREFIX + 'lang-search{position:relative;margin-bottom:9px;}' +
    '.' + PREFIX + 'lang-search input{width:100%;height:40px;padding:0 14px 0 38px;border:1.5px solid var(--aw-hair-2);border-radius:11px;font-size:14px;color:var(--aw-ink);background:var(--aw-surface);}' +
    '.' + PREFIX + 'lang-search input:focus{outline:none;border-color:var(--aw-accent);}' +
    '.' + PREFIX + 'lang-search svg{position:absolute;left:13px;top:50%;transform:translateY(-50%);width:16px;height:16px;stroke:var(--aw-muted);stroke-width:2;fill:none;}' +
    '.' + PREFIX + 'lang-list{max-height:258px;overflow-y:auto;border-radius:11px;background:var(--aw-surface-2);}' +
    '.' + PREFIX + 'lang-opt{display:flex;align-items:center;gap:11px;width:100%;padding:10px 13px;font-size:14px;color:var(--aw-ink);text-align:left;border-top:1px solid var(--aw-hair);transition:background .12s ease;}' +
    '.' + PREFIX + 'lang-opt:first-child{border-top:none;}' +
    '.' + PREFIX + 'lang-opt:hover{background:var(--aw-surface-3);}' +
    '.' + PREFIX + 'lang-opt:focus-visible{outline:3px solid var(--aw-focus);outline-offset:-3px;}' +
    '.' + PREFIX + 'lang-opt.' + PREFIX + 'sel{color:var(--aw-accent);font-weight:700;}' +
    '.' + PREFIX + 'check{margin-left:auto;color:var(--aw-accent);font-weight:700;display:flex;}' +
    '.' + PREFIX + 'check svg{width:17px;height:17px;stroke:var(--aw-accent);stroke-width:2.4;fill:none;}' +

    /* ---------- page-structure dialog ---------- */
    '.' + PREFIX + 'dialog{position:fixed;inset:0;z-index:' + (z + 3) + ';display:none;align-items:center;justify-content:center;background:rgba(15,18,24,.5);padding:16px;}' +
    '.' + PREFIX + 'dialog.' + PREFIX + 'open{display:flex;}' +
    '.' + PREFIX + 'dialog-box{background:var(--aw-surface);border-radius:var(--aw-r-md);width:min(520px,94vw);max-height:82vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:var(--aw-shadow);}' +
    '.' + PREFIX + 'dialog-head{display:flex;align-items:center;justify-content:space-between;padding:15px 16px;border-bottom:1px solid var(--aw-hair);}' +
    '.' + PREFIX + 'dialog-head h3{font-size:16px;font-weight:750;color:var(--aw-ink);letter-spacing:-.01em;}' +
    '.' + PREFIX + 'tabs{display:flex;gap:6px;padding:10px 12px 0;}' +
    '.' + PREFIX + 'tab{flex:1;height:36px;border-radius:10px;background:var(--aw-surface-2);font-size:13px;font-weight:650;color:var(--aw-muted);transition:background .14s ease,color .14s ease;}' +
    '.' + PREFIX + 'tab.' + PREFIX + 'active{background:var(--aw-accent);color:#fff;}' +
    '.' + PREFIX + 'tab:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +
    '.' + PREFIX + 'dialog-body{overflow-y:auto;padding:12px;}' +
    '.' + PREFIX + 'struct-item{display:flex;align-items:center;width:100%;text-align:left;background:var(--aw-surface-2);border-radius:10px;padding:11px 12px;margin-bottom:7px;font-size:13px;color:var(--aw-ink);transition:background .12s ease;}' +
    '.' + PREFIX + 'struct-item:hover{background:var(--aw-surface-3);}' +
    '.' + PREFIX + 'struct-item:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +
    '.' + PREFIX + 'tag{flex:0 0 auto;min-width:34px;text-align:center;font-size:10px;font-weight:700;color:#fff;background:var(--aw-accent);border-radius:6px;padding:2px 6px;margin-right:10px;text-transform:uppercase;}' +

    /* ---------- RTL ---------- */
    '.' + PREFIX + 'panel[dir="rtl"]{right:auto;left:0;transform:translateX(-102%);border-radius:0 var(--aw-r-lg) var(--aw-r-lg) 0;}' +
    '.' + PREFIX + 'panel[dir="rtl"].' + PREFIX + 'open{transform:translateX(0);}' +
    '.' + PREFIX + 'panel[dir="rtl"] .' + PREFIX + 'trigger{right:auto;left:22px;}' +
    '.' + PREFIX + 'panel[dir="rtl"] .' + PREFIX + 'caret,.' + PREFIX + 'panel[dir="rtl"] .' + PREFIX + 'switch,.' + PREFIX + 'panel[dir="rtl"] .' + PREFIX + 'sw-clear{margin-left:0;margin-right:auto;}' +

    /* ---------- read-aloud (TTS) bar ---------- */
    '.' + PREFIX + 'speech-bar{position:fixed;bottom:max(90px,calc(env(safe-area-inset-bottom) + 74px));right:24px;z-index:' + (z + 2) + ';background:var(--aw-surface);color:var(--aw-ink);border:1px solid var(--aw-hair);border-radius:15px;padding:9px 11px;box-shadow:var(--aw-shadow);display:none;align-items:center;gap:7px;flex-wrap:wrap;max-width:92vw;font-size:13px;}' +
    '.' + PREFIX + 'sbtn{display:inline-flex;align-items:center;gap:7px;border-radius:10px;font-size:12.5px;font-weight:650;padding:8px 13px;background:var(--aw-surface-2);color:var(--aw-ink);transition:background .14s ease,transform .1s ease;}' +
    '.' + PREFIX + 'sbtn:hover{background:var(--aw-surface-3);}' +
    '.' + PREFIX + 'sbtn:active{transform:scale(.95);}' +
    '.' + PREFIX + 'sbtn:focus-visible{outline:3px solid var(--aw-focus);outline-offset:2px;}' +
    '.' + PREFIX + 'sbtn svg{width:16px;height:16px;stroke:currentColor;stroke-width:1.9;fill:none;}' +
    '.' + PREFIX + 'sbtn-primary{background:var(--aw-accent);color:#fff;}' +
    '.' + PREFIX + 'sbtn-primary:hover{background:var(--aw-accent-press);}' +
    '.' + PREFIX + 'sbtn-primary svg{stroke:#fff;}' +
    '.' + PREFIX + 'sbtn-icon{width:38px;height:38px;padding:0;justify-content:center;}' +
    '.' + PREFIX + 'sbtn-icon svg{fill:currentColor;stroke:none;}' +
    /* when the panel is open, keep the read-aloud bar clear of the footer/Reset */
    '#' + WIDGET_ID + '.' + PREFIX + 'panel-open .' + PREFIX + 'speech-bar{bottom:max(160px,calc(env(safe-area-inset-bottom) + 160px));}' +
    '@media (min-width:641px){#' + WIDGET_ID + '.' + PREFIX + 'panel-open .' + PREFIX + 'speech-bar{right:auto;left:24px;bottom:max(24px,env(safe-area-inset-bottom));}}' +

    /* ---------- translation toast ---------- */
    '.' + PREFIX + 'trans-toast{position:fixed;bottom:max(96px,calc(env(safe-area-inset-bottom) + 80px));left:50%;transform:translateX(-50%);z-index:' + (z + 4) + ';background:' + c + ';color:#fff;padding:11px 18px;border-radius:999px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;font-size:13px;font-weight:650;display:none;align-items:center;gap:10px;box-shadow:0 8px 22px rgba(20,26,38,.32);max-width:90vw;text-align:center;}' +
    '.' + PREFIX + 'trans-toast.' + PREFIX + 'show{display:flex;}' +
    '.' + PREFIX + 'trans-spin{width:14px;height:14px;border:2px solid rgba(255,255,255,.4);border-top-color:#fff;border-radius:50%;animation:' + PREFIX + 'spin .7s linear infinite;flex:0 0 auto;}' +
    '@keyframes ' + PREFIX + 'spin{to{transform:rotate(360deg);}}' +

    /* ---------- reduced motion ---------- */
    '@media (prefers-reduced-motion:reduce){#' + WIDGET_ID + ' *{transition-duration:.001ms !important;animation-duration:.001ms !important;scroll-behavior:auto !important;}}' +

    /* ---------- MOBILE: bottom sheet ---------- */
    '@media (max-width:640px){' +
      '.' + PREFIX + 'panel,.' + PREFIX + 'panel.' + PREFIX + 'xl{width:100vw;height:auto;max-height:92dvh;top:auto;bottom:0;left:0;right:0;border-radius:var(--aw-r-lg) var(--aw-r-lg) 0 0;transform:translateY(102%);}' +
      '.' + PREFIX + 'panel.' + PREFIX + 'open,.' + PREFIX + 'panel[dir="rtl"].' + PREFIX + 'open{transform:translateY(0);}' +
      '.' + PREFIX + 'panel[dir="rtl"]{transform:translateY(102%);border-radius:var(--aw-r-lg) var(--aw-r-lg) 0 0;}' +
      '.' + PREFIX + 'grip{display:flex;justify-content:center;padding:9px 0 3px;flex:0 0 auto;background:var(--aw-surface);}' +
      '.' + PREFIX + 'grip span{width:38px;height:5px;border-radius:999px;background:var(--aw-hair-2);}' +
      '.' + PREFIX + 'header{padding-top:8px;}' +
      '.' + PREFIX + 'grid{grid-template-columns:repeat(3,1fr);}' +
      '.' + PREFIX + 'tile{min-height:88px;}' +
    '}' +
    '@media (max-width:380px){.' + PREFIX + 'grid{grid-template-columns:repeat(2,1fr);}}';

    injectStyle('widget-chrome', css);
  }

  /* Colour helpers (no deps): darken/lighten and tint toward white. */
  function clampByte(n) { return n < 0 ? 0 : n > 255 ? 255 : Math.round(n); }
  function hexToRgb(hex) {
    var h = String(hex).replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (v) { var s = clampByte(v).toString(16); return s.length === 1 ? '0' + s : s; }).join('');
  }
  function shade(hex, pct) {
    var c = hexToRgb(hex); var p = pct / 100; var t = p < 0 ? 0 : 255; var a = Math.abs(p);
    return rgbToHex(c.r + (t - c.r) * a, c.g + (t - c.g) * a, c.b + (t - c.b) * a);
  }
  function tint(hex, amt) {
    var c = hexToRgb(hex);
    return rgbToHex(c.r + (255 - c.r) * amt, c.g + (255 - c.g) * amt, c.b + (255 - c.b) * amt);
  }
  /* ================================================================
     ICONS  (inline stroke/solid SVG; colour comes from CSS)
     ================================================================ */
  var ICONS = {
    accessibility: '<svg viewBox="0 0 24 24"><circle cx="12" cy="4.4" r="2.1" fill="currentColor" stroke="none"/><path d="M4.5 8.6h15M12 8.6v6.4M8.4 21l3.6-6 3.6 6"/></svg>',
    contrast: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none"/></svg>',
    contrastPlus: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none"/></svg>',
    pause: '<svg viewBox="0 0 24 24"><rect x="7" y="6" width="3.5" height="12" rx="1"/><rect x="13.5" y="6" width="3.5" height="12" rx="1"/></svg>',
    speaker: '<svg viewBox="0 0 24 24"><path d="M5 9v6h4l5 4V5L9 9H5z"/><path d="M17 9c1 1.5 1 4.5 0 6"/></svg>',
    mic: '<svg viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/></svg>',
    link: '<svg viewBox="0 0 24 24"><path d="M9 13a4 4 0 0 0 5.6.6l3-3A4 4 0 0 0 12 5l-1.5 1.5M15 11a4 4 0 0 0-5.6-.6l-3 3A4 4 0 0 0 12 19l1.5-1.5"/></svg>',
    biggerText: '<svg viewBox="0 0 24 24"><path d="M3 8V6h7v2M6.5 6v12M13 9V7.5h6V9M16 7.5v10.5"/></svg>',
    spacing: '<svg viewBox="0 0 24 24"><path d="M3 12h18M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3"/></svg>',
    hideImg: '<svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 5l18 14"/><circle cx="8.5" cy="10" r="1.5"/></svg>',
    dyslexia: '<svg viewBox="0 0 24 24"><path d="M4 18V6h4a4 4 0 0 1 0 8H4M16 6v12M13 6h6"/></svg>',
    cursor: '<svg viewBox="0 0 24 24"><path d="M6 4l13 7-5 1.5L11 19 6 4z"/></svg>',
    tooltip: '<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M8 17l3 3 3-3"/><path d="M12 8v.5M12 11v2"/></svg>',
    structure: '<svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="5" rx="1"/><rect x="4" y="12" width="7" height="8" rx="1"/><rect x="14" y="12" width="6" height="8" rx="1"/></svg>',
    lineHeight: '<svg viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16M2 4v16"/></svg>',
    textAlign: '<svg viewBox="0 0 24 24"><path d="M5 6h14M5 10h9M5 14h14M5 18h9"/></svg>',
    alignLeft: '<svg viewBox="0 0 24 24"><path d="M4 6h16M4 10h9M4 14h16M4 18h9"/></svg>',
    alignCenter: '<svg viewBox="0 0 24 24"><path d="M4 6h16M7 10h10M4 14h16M7 18h10"/></svg>',
    alignRight: '<svg viewBox="0 0 24 24"><path d="M4 6h16M11 10h9M4 14h16M11 18h9"/></svg>',
    dictionary: '<svg viewBox="0 0 24 24"><path d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2V4z"/><path d="M9 8h6M9 11h4"/></svg>',
    saturation: '<svg viewBox="0 0 24 24"><path d="M12 3s6 6 6 10a6 6 0 0 1-12 0c0-4 6-10 6-10z"/></svg>',
    moon: '<svg viewBox="0 0 24 24"><path d="M20 14a8 8 0 0 1-10-10 8 8 0 1 0 10 10z"/></svg>',
    sun: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19"/></svg>',
    eye: '<svg viewBox="0 0 24 24"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>',
    palette: '<svg viewBox="0 0 24 24"><path d="M12 3a9 9 0 0 0 0 18c1.5 0 2-1 2-2s-1-1.5-1-2.5 1-1.5 2-1.5h2a4 4 0 0 0 4-4c0-4.5-4.5-8-9-8z"/><circle cx="7.5" cy="11" r="1"/><circle cx="12" cy="8" r="1"/><circle cx="16" cy="11" r="1"/></svg>',
    mute: '<svg viewBox="0 0 24 24"><path d="M5 9v6h4l5 4V5L9 9H5z"/><path d="M16 9l5 6M21 9l-5 6"/></svg>',
    readMode: '<svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M8 8h8M8 12h8M8 16h5"/></svg>',
    guide: '<svg viewBox="0 0 24 24"><path d="M12 4v10M8 10l4 4 4-4"/><path d="M4 20h16"/></svg>',
    mask: '<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><rect x="3" y="10" width="18" height="4" fill="currentColor" stroke="none" opacity="0.25"/></svg>',
    stop: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M9 9l6 6M15 9l-6 6"/></svg>',
    hover: '<svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M4 9h16M9 4v16"/></svg>',
    focus: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M12 3v3M12 18v3M3 12h3M18 12h3"/></svg>',
    cursorBlack: '<svg viewBox="0 0 24 24"><path d="M6 4l13 7-5 1.5L11 19 6 4z" fill="currentColor"/></svg>',
    cursorWhite: '<svg viewBox="0 0 24 24"><path d="M6 4l13 7-5 1.5L11 19 6 4z" fill="#fff"/></svg>',
    magnifier: '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="6"/><path d="M16 16l4 4M9 11h4M11 9v4"/></svg>',
    titles: '<svg viewBox="0 0 24 24"><path d="M5 6h14M12 6v12"/></svg>',
    font: '<svg viewBox="0 0 24 24"><path d="M4 18L9 6l5 12M6 14h6"/><path d="M16 18l2-6 2 6M16.5 16h3"/></svg>',
    keyboard: '<svg viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="11" rx="2"/><path d="M7 11h.01M11 11h.01M15 11h.01M7 14h10"/></svg>',
    scale: '<svg viewBox="0 0 24 24"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
    position: '<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><rect x="13" y="8" width="6" height="8" rx="1" fill="currentColor" stroke="none"/></svg>',
    search: '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M16 16l5 5"/></svg>',
    shield: '<svg viewBox="0 0 24 24"><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z"/></svg>',
    target: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none"/></svg>',
    glasses: '<svg viewBox="0 0 24 24"><circle cx="6.5" cy="14" r="3.2"/><circle cx="17.5" cy="14" r="3.2"/><path d="M9.7 14c0-1 4.6-1 4.6 0M3.3 14L5 7M20.7 14L19 7"/></svg>',
    chevronRight: '<svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6"/></svg>',
    xMark: '<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    reset: '<svg viewBox="0 0 24 24"><path d="M4 4v6h6M20 20v-6h-6"/><path d="M20 10a8 8 0 0 0-14.3-3M4 14a8 8 0 0 0 14.3 3"/></svg>',
    globe: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/></svg>',
    sliders: '<svg viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/><circle cx="9" cy="6" r="2" fill="var(--aw-surface)"/><circle cx="15" cy="12" r="2" fill="var(--aw-surface)"/><circle cx="8" cy="18" r="2" fill="var(--aw-surface)"/></svg>',
    check: '<svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7"/></svg>',
    play: '<svg viewBox="0 0 24 24"><path d="M7 5l12 7-12 7V5z" fill="currentColor" stroke="none"/></svg>',
    pauseBars: '<svg viewBox="0 0 24 24"><rect x="7" y="5" width="3.5" height="14" rx="1" fill="currentColor" stroke="none"/><rect x="13.5" y="5" width="3.5" height="14" rx="1" fill="currentColor" stroke="none"/></svg>',
    stopSq: '<svg viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none"/></svg>'
  };
  function ic(name) { return ICONS[name] || ICONS.accessibility; }

  /* Profile id → icon name (replaces emoji for a crisp, consistent look) */
  var PROFILE_ICONS = {
    seizureSafe: 'shield', visionImpaired: 'eye', adhdFriendly: 'target', cognitive: 'readMode',
    keyboardNav: 'keyboard', screenReaderP: 'speaker', olderAdults: 'glasses', colorBlind: 'palette',
    dyslexia: 'dyslexia', motorImpaired: 'cursor'
  };

  /* ================================================================
     TINY DOM HELPER
     ================================================================ */
  function el(tag, props, kids) {
    var n = document.createElement(tag);
    if (props) Object.keys(props).forEach(function (k) {
      if (k === 'class') n.className = props[k];
      else if (k === 'html') n.innerHTML = props[k];
      else if (k === 'text') n.textContent = props[k];
      else if (k.slice(0, 2) === 'on' && typeof props[k] === 'function') n.addEventListener(k.slice(2).toLowerCase(), props[k]);
      else if (props[k] != null) n.setAttribute(k, props[k]);
    });
    if (kids) (Array.isArray(kids) ? kids : [kids]).forEach(function (c) {
      if (c == null) return;
      n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return n;
  }
  function searchTag(node, text) { if (node && text) node.setAttribute('data-search', String(text).toLowerCase()); return node; }

  /* ================================================================
     CONTROL BUILDERS
     ================================================================ */
  function makeSection(titleKey, kids, opts) {
    opts = opts || {};
    var attrs = { 'class': PREFIX + 'section' + (opts.prominent ? ' ' + PREFIX + 'prominent' : ''), 'aria-label': t(titleKey) };
    if (opts.id) attrs.id = PREFIX + 'sec-' + opts.id;
    if (opts.cat) { attrs['data-cat'] = opts.cat; if (opts.icon) attrs['data-icon'] = opts.icon; }
    var head = el('div', { 'class': PREFIX + 'sec-head' }, [
      opts.icon ? el('span', { 'class': PREFIX + 'sec-ic', html: ic(opts.icon) }) : null,
      el('h3', { 'class': PREFIX + 'section-title', text: t(titleKey) })
    ]);
    var children = [head];
    if (opts.sub) children.push(el('p', { 'class': PREFIX + 'sec-sub', text: t(opts.sub) }));
    return el('section', attrs, children.concat(kids || []));
  }

  function makeGrid(tiles) { return el('div', { 'class': PREFIX + 'grid' }, tiles); }

  /* tile for a control that cycles through several levels on each tap.
     Shows the current level as a caption + pips so it's clear more taps = more options. */
  function makeCycleTile(iconName, labelKey, levels, idx, levelLabels, onClick) {
    if (idx < 0) idx = 0;
    var active = idx > 0;
    var label = t(labelKey);
    var pips = el('span', { 'class': PREFIX + 'pips', 'aria-hidden': 'true' },
      levels.map(function (_, i) { return el('span', { 'class': PREFIX + 'pip' + (i <= idx ? ' ' + PREFIX + 'pip-on' : '') }); }));
    var tile = el('button', {
      type: 'button', 'class': PREFIX + 'tile' + (active ? ' ' + PREFIX + 'active' : ''),
      'aria-pressed': active ? 'true' : 'false',
      'aria-label': label + ': ' + levelLabels[idx] + '. ' + t('tapToCycle'), onClick: onClick
    }, [
      el('span', { 'class': PREFIX + 'dot' }),
      el('span', { 'class': PREFIX + 'ic', html: ic(iconName) }),
      el('span', { 'class': PREFIX + 'lab', text: label }),
      el('span', { 'class': PREFIX + 'cap', text: levelLabels[idx] }),
      pips
    ]);
    return searchTag(tile, label + ' ' + levelLabels.join(' '));
  }

  /* toggle/enum tile — pressed = active */
  function makeTile(iconName, labelKey, active, onClick, opts) {
    opts = opts || {};
    var label = opts.label || t(labelKey);
    var cls = PREFIX + 'tile' + (active ? ' ' + PREFIX + 'active' : '') + (opts.span2 ? ' ' + PREFIX + 'span2' : '');
    var tile = el('button', {
      type: 'button', 'class': cls, 'aria-pressed': active ? 'true' : 'false', 'aria-label': label, onClick: onClick
    }, [
      el('span', { 'class': PREFIX + 'dot' }),
      el('span', { 'class': PREFIX + 'ic', html: ic(iconName) }),
      el('span', { 'class': PREFIX + 'lab', text: label })
    ]);
    return searchTag(tile, label);
  }

  /* stepper card */
  function makeStepper(iconName, labelKey, valueText, onDec, onInc, decOff, incOff) {
    var valSpan = el('span', { 'class': PREFIX + 'val', 'aria-live': 'polite', text: valueText });
    var dec = el('button', { type: 'button', 'aria-label': t(labelKey) + ' decrease', text: '\u2212', onClick: onDec });
    var inc = el('button', { type: 'button', 'aria-label': t(labelKey) + ' increase', text: '+', onClick: onInc });
    if (decOff) dec.setAttribute('disabled', 'disabled');
    if (incOff) inc.setAttribute('disabled', 'disabled');
    var wrap = el('div', { 'class': PREFIX + 'stepper-wrap' }, [
      el('div', { 'class': PREFIX + 'stepper-head' }, [
        el('span', { 'class': PREFIX + 'ic', html: ic(iconName) }),
        document.createTextNode(t(labelKey))
      ]),
      el('div', { 'class': PREFIX + 'stepper' }, [dec, valSpan, inc])
    ]);
    return searchTag(wrap, t(labelKey));
  }

  /* generic on/off switch row (oversized) */
  function makeSwitchRow(labelNode, checked, onChange) {
    var input = el('input', { type: 'checkbox' });
    input.checked = !!checked;
    input.addEventListener('change', function () { onChange(input.checked); });
    var sw = el('label', { 'class': PREFIX + 'switch' }, [input, el('span', { 'class': PREFIX + 'slider' })]);
    return el('div', { 'class': PREFIX + 'xl-row' }, [labelNode, sw]);
  }

  /* colour swatch picker */
  function makeColorPicker(titleKey, current, onPick, onClear) {
    var palette = ['#1d6fb8', '#7e57c2', '#d64545', '#d97706', '#0d9488', '#4d7c2f', '#f1f1f1', '#111111'];
    var sws = palette.map(function (hex) {
      var sel = current && current.toLowerCase() === hex.toLowerCase();
      return el('button', {
        type: 'button', 'class': PREFIX + 'sw' + (sel ? ' ' + PREFIX + 'sel' : ''),
        style: 'background:' + hex, 'aria-label': t(titleKey) + ' ' + hex,
        'aria-pressed': sel ? 'true' : 'false', onClick: function () { onPick(hex); }
      });
    });
    var card = el('div', { 'class': PREFIX + 'swatch-card ' + PREFIX + 'span3' }, [
      el('div', { 'class': PREFIX + 'sw-title' }, [
        el('span', { text: t(titleKey) }),
        el('button', { type: 'button', 'class': PREFIX + 'sw-clear', text: t('defaultLabel'), 'aria-label': t(titleKey) + ' ' + t('defaultLabel'), onClick: onClear })
      ]),
      el('div', { 'class': PREFIX + 'swatches' }, sws)
    ]);
    return searchTag(card, t(titleKey));
  }

  /* accessibility-profile row with iOS-style switch */
  function makeProfileRow(id) {
    var p = PROFILES[id];
    var on = prefs.activeProfiles.indexOf(id) > -1;
    var sw = el('button', {
      type: 'button', role: 'switch', 'aria-checked': on ? 'true' : 'false',
      'class': PREFIX + 'seg' + (on ? ' ' + PREFIX + 'on' : ''), 'aria-label': t(p.label),
      onClick: function () { toggleProfile(id, !on); }
    }, [el('span', { 'class': PREFIX + 'knob' })]);
    var row = el('div', { 'class': PREFIX + 'prow' + (on ? ' ' + PREFIX + 'pon' : '') }, [
      el('span', { 'class': PREFIX + 'picon', html: ic(PROFILE_ICONS[id] || 'accessibility') }),
      el('div', { 'class': PREFIX + 'pmeta' }, [
        el('div', { 'class': PREFIX + 'pname', text: t(p.label) }),
        el('div', { 'class': PREFIX + 'pdesc', text: t(p.desc) })
      ]),
      sw
    ]);
    return searchTag(row, t(p.label) + ' ' + t(p.desc));
  }

  /* set a single pref then persist+apply+re-render */
  function setPref(key, value) {
    prefs[key] = value;
    if (key === 'cursorStyle') prefs.bigCursor = (value !== 'default');
    savePrefs();
    applyAll();
    renderControls();
    if (typeof config.onChange === 'function') config.onChange(key, value, prefs);
  }
  function toggleBool(key) { setPref(key, !prefs[key]); announceToggle(t(labelForKey(key)), prefs[key]); }
  function toggleEnum(key, value) { setPref(key, prefs[key] === value ? defaults[key] : value); }
  function labelForKey(k) {
    var map = { readableFont: 'readableFont', dyslexiaFont: 'dyslexiaFont', highlightTitles: 'highlightTitles',
      textMagnifier: 'textMagnifier', linkHighlight: 'highlightLinks', hideImages: 'hideImages', muteSounds: 'muteSounds',
      readMode: 'readMode', readingGuide: 'readingGuide', readingMask: 'readingMask', stopAnimations: 'stopAnimations',
      highlightHover: 'highlightHover', highlightFocus: 'highlightFocus', tooltips: 'tooltips', screenReader: 'screenReaderTTS',
      voiceNavigation: 'voiceNavigation', keyboardNav: 'keyboardNavigation', dictionary: 'dictionary' };
    return map[k] || k;
  }

  /* ================================================================
     LANGUAGE SELECTOR
     ================================================================ */
  function buildLanguageSelector() {
    var meta = langMeta(prefs.language);
    var btn = el('button', { type: 'button', 'class': PREFIX + 'lang-btn', 'aria-haspopup': 'true', 'aria-expanded': 'false' }, [
      el('span', { 'class': PREFIX + 'flag', text: meta.flag }),
      el('span', { text: meta.name }),
      el('span', { 'class': PREFIX + 'caret', html: '<svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6"/></svg>' })
    ]);
    var input = el('input', { type: 'text', 'aria-label': t('searchLanguage'), placeholder: t('searchLanguage') });
    var list = el('div', { 'class': PREFIX + 'lang-list', role: 'listbox', 'aria-label': t('language') });

    function renderList(filter) {
      list.innerHTML = '';
      var f = (filter || '').toLowerCase();
      LANGUAGES.filter(function (l) { return !f || l.name.toLowerCase().indexOf(f) > -1 || l.code.indexOf(f) > -1; })
        .forEach(function (l) {
          var sel = l.code === prefs.language;
          list.appendChild(el('button', {
            type: 'button', 'class': PREFIX + 'lang-opt' + (sel ? ' ' + PREFIX + 'sel' : ''),
            role: 'option', 'aria-selected': sel ? 'true' : 'false',
            onClick: function () { setPref('language', l.code); applyLanguage(); translatePage(l.code); renderControls(); }
          }, [
            el('span', { 'class': PREFIX + 'flag', text: l.flag }),
            el('span', { text: l.name }),
            sel ? el('span', { 'class': PREFIX + 'check', html: ic('check') }) : null
          ]));
        });
    }
    renderList('');
    input.addEventListener('input', function () { renderList(input.value); });

    var pop = el('div', { 'class': PREFIX + 'lang-pop' }, [
      el('div', { 'class': PREFIX + 'lang-search' }, [input, el('span', { html: ic('search') })]),
      list
    ]);
    btn.addEventListener('click', function () {
      var open = pop.classList.toggle(PREFIX + 'open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) setTimeout(function () { input.focus(); }, 30);
    });
    return searchTag(el('div', {}, [btn, pop]), t('language') + ' ' + t('translateNote'));
  }

  /* ================================================================
     USEFUL LINKS  (prominent, scrollable list — supports many links)
     ================================================================ */
  function buildUsefulLinks() {
    var rows = config.usefulLinks.map(function (l) {
      var row = el('button', { type: 'button', 'class': PREFIX + 'link-row', 'aria-label': l.label }, [
        el('span', { 'class': PREFIX + 'link-ic', html: ic(l.icon && ICONS[l.icon] ? l.icon : 'link') }),
        el('span', { 'class': PREFIX + 'link-label', text: l.label }),
        el('span', { 'class': PREFIX + 'link-go', html: ic('chevronRight') })
      ]);
      row.addEventListener('click', function () {
        var href = l.href || '';
        var target = null;
        if (href.charAt(0) === '#') { try { target = document.querySelector(href) || document.getElementById(href.slice(1)); } catch (e) {} }
        if (target) { closePanel(); target.setAttribute('tabindex', '-1'); try { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { target.scrollIntoView(); } target.focus(); }
        else if (href) { window.open(href, l.external === false ? '_self' : (l.external ? '_blank' : '_self')); }
      });
      return searchTag(row, l.label);
    });
    return el('div', { 'class': PREFIX + 'links', role: 'list' }, rows);
  }

  /* ================================================================
     PAGE STRUCTURE DIALOG
     ================================================================ */
  var structDialog = null;
  function openStructure() {
    if (!structDialog) buildStructureDialog();
    fillStructure('headings');
    structDialog.classList.add(PREFIX + 'open');
    var firstTab = structDialog.querySelector('.' + PREFIX + 'tab');
    if (firstTab) firstTab.focus();
  }
  function closeStructure() { if (structDialog) structDialog.classList.remove(PREFIX + 'open'); }
  function buildStructureDialog() {
    var body = el('div', { 'class': PREFIX + 'dialog-body' });
    function tab(key) {
      return el('button', { type: 'button', 'class': PREFIX + 'tab', 'data-tab': key, text: t(key), onClick: function () {
        Array.prototype.forEach.call(structDialog.querySelectorAll('.' + PREFIX + 'tab'), function (b) { b.classList.remove(PREFIX + 'active'); });
        this.classList.add(PREFIX + 'active'); fillStructure(key);
      } });
    }
    var tabs = el('div', { 'class': PREFIX + 'tabs' }, [tab('headings'), tab('landmarks'), tab('links')]);
    var box = el('div', { 'class': PREFIX + 'dialog-box', role: 'dialog', 'aria-modal': 'true', 'aria-label': t('pageStructure') }, [
      el('div', { 'class': PREFIX + 'dialog-head' }, [
        el('h3', { text: t('pageStructure') }),
        el('button', { type: 'button', 'class': PREFIX + 'close', 'aria-label': t('close'), html: ic('xMark'), onClick: closeStructure })
      ]),
      tabs, body
    ]);
    structDialog = el('div', { 'class': PREFIX + 'dialog', onClick: function (e) { if (e.target === structDialog) closeStructure(); } }, [box]);
    structDialog._body = body;
    (widgetRoot || document.documentElement).appendChild(structDialog);
    tabs.firstChild.classList.add(PREFIX + 'active');
  }
  function fillStructure(kind) {
    var body = structDialog._body; body.innerHTML = '';
    var nodes = [];
    if (kind === 'headings') nodes = Array.prototype.slice.call(document.querySelectorAll('h1,h2,h3,h4,h5,h6'));
    else if (kind === 'landmarks') nodes = Array.prototype.slice.call(document.querySelectorAll('main,nav,header,footer,aside,section[aria-label],[role="main"],[role="navigation"],[role="banner"],[role="contentinfo"]'));
    else nodes = Array.prototype.slice.call(document.querySelectorAll('a[href]'));
    nodes = nodes.filter(function (n) { return !n.closest('#' + WIDGET_ID); }).slice(0, 150);
    if (!nodes.length) { body.appendChild(el('p', { 'class': PREFIX + 'sec-sub', style: 'text-align:center;padding:14px', text: t('noItems') })); return; }
    nodes.forEach(function (n) {
      var tag = kind === 'links' ? 'link' : (n.getAttribute('role') || n.tagName.toLowerCase());
      var label = (n.textContent || n.getAttribute('aria-label') || '').trim().replace(/\s+/g, ' ').slice(0, 90) || '(' + tag + ')';
      body.appendChild(el('button', { type: 'button', 'class': PREFIX + 'struct-item', onClick: function () {
        closeStructure(); closePanel();
        n.setAttribute('tabindex', '-1');
        try { n.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { n.scrollIntoView(); }
        n.focus();
      } }, [el('span', { 'class': PREFIX + 'tag', text: kind === 'headings' ? n.tagName : tag }), document.createTextNode(label)]));
    });
  }

  /* ================================================================
     BUILD THE WIDGET SHELL
     ================================================================ */
  function buildWidget() {
    widgetRoot = el('div', { id: WIDGET_ID });

    triggerBtn = el('button', {
      type: 'button', 'class': PREFIX + 'trigger', 'aria-haspopup': 'dialog', 'aria-expanded': 'false',
      'aria-label': t('panelTitle'), accesskey: config.accessKey, title: t('panelTitle') + ' (' + shortcutLabel() + ')',
      html: ic('accessibility')
    });
    triggerBtn.addEventListener('click', togglePanel);

    var backdrop = el('div', { 'class': PREFIX + 'backdrop', onClick: closePanel });

    var grip = el('div', { 'class': PREFIX + 'grip', 'aria-hidden': 'true' }, [el('span')]);

    var header = el('div', { 'class': PREFIX + 'header' }, [
      el('span', { 'class': PREFIX + 'brand', 'aria-hidden': 'true', html: ic('accessibility') }),
      el('h2', { 'class': PREFIX + 'title', id: PREFIX + 'title' }, [
        el('span', { 'class': PREFIX + 'title-text', text: config.labels.panelTitle || t('panelTitle') }),
        el('small', { 'class': PREFIX + 'title-sub', text: t('customize') })
      ]),
      el('button', { type: 'button', 'class': PREFIX + 'close', 'aria-label': t('close'), html: ic('xMark'), onClick: closePanel })
    ]);

    var searchInput = el('input', { type: 'text', 'aria-label': t('searchSettings'), placeholder: t('searchSettings') });
    var searchClear = el('button', { type: 'button', 'class': PREFIX + 'search-clear', 'aria-label': t('clear'), html: ic('xMark') });
    var chips = el('div', { 'class': PREFIX + 'chips', role: 'tablist', 'aria-label': t('panelTitle') });
    enableHScroll(chips);
    var subhead = el('div', { 'class': PREFIX + 'subhead' }, [
      el('div', { 'class': PREFIX + 'search' }, [el('span', { html: ic('search') }), searchInput, searchClear]),
      chips
    ]);
    searchInput.addEventListener('input', function () {
      searchClear.classList.toggle(PREFIX + 'show', !!searchInput.value);
      applySearch(searchInput.value);
    });
    searchClear.addEventListener('click', function () { searchInput.value = ''; searchClear.classList.remove(PREFIX + 'show'); applySearch(''); searchInput.focus(); });

    var body = el('div', { 'class': PREFIX + 'body', id: PREFIX + 'panel-body' });
    var empty = el('div', { 'class': PREFIX + 'empty' }, [el('span', { html: ic('search') }), el('p', { text: t('noResults') })]);

    var footer = el('div', { 'class': PREFIX + 'footer' }, [
      el('button', { type: 'button', 'class': PREFIX + 'reset', onClick: function () {
        resetPrefs(); applyAll(); applyLanguage(); renderControls(); announce(t('reset'));
      } }, [el('span', { html: ic('reset') }), el('span', { 'class': PREFIX + 'reset-text', text: t('reset') })]),
      el('p', { 'class': PREFIX + 'disclaimer', text: t('disclaimer') }),
      config.openShortcut ? el('p', { 'class': PREFIX + 'hint', text: t('shortcutHint') }) : null
    ]);

    panel = el('div', {
      'class': PREFIX + 'panel', role: 'dialog', 'aria-modal': 'false',
      'aria-labelledby': PREFIX + 'title', 'aria-hidden': 'true', dir: langMeta(prefs.language).dir
    }, [grip, header, subhead, body, empty, footer]);
    panel._body = body; panel._chips = chips; panel._search = searchInput; panel._searchClear = searchClear; panel._empty = empty;

    liveRegion = el('div', { 'aria-live': 'polite', 'aria-atomic': 'true', style: 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;' });

    widgetRoot.appendChild(triggerBtn);
    widgetRoot.appendChild(backdrop);
    widgetRoot.appendChild(panel);
    widgetRoot.appendChild(liveRegion);
    widgetRoot._backdrop = backdrop;
    document.documentElement.appendChild(widgetRoot);
  }

  /* ================================================================
     CATEGORY CHIPS + SEARCH FILTER
     ================================================================ */
  /* Let a horizontally-overflowing row scroll with a vertical mouse wheel and
     with click-and-drag (touch already scrolls natively). */
  function enableHScroll(box) {
    if (!box || box._hscroll) return; box._hscroll = true;
    box.addEventListener('wheel', function (e) {
      if (box.scrollWidth <= box.clientWidth) return;
      var d = Math.abs(e.deltaX) >= Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!d) return;
      box.scrollLeft += d;
      e.preventDefault();
    }, { passive: false });
    var down = false, startX = 0, startLeft = 0, moved = false, pid = null, captured = false;
    box.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || box.scrollWidth <= box.clientWidth) return;
      down = true; moved = false; captured = false; pid = e.pointerId; startX = e.clientX; startLeft = box.scrollLeft;
    });
    box.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      /* only start dragging (and capture the pointer) once past a small threshold,
         so a normal tap/click still reaches the chip and triggers its action */
      if (!moved && Math.abs(dx) > 4) { moved = true; box.classList.add(PREFIX + 'dragging'); try { box.setPointerCapture(pid); captured = true; } catch (err) {} }
      if (moved) { box.scrollLeft = startLeft - dx; e.preventDefault(); }
    });
    function end() {
      if (!down) return; down = false;
      box.classList.remove(PREFIX + 'dragging');
      if (captured) { try { box.releasePointerCapture(pid); } catch (err) {} captured = false; }
    }
    box.addEventListener('pointerup', end);
    box.addEventListener('pointercancel', end);
    document.addEventListener('pointerup', end);
    /* swallow the click that ends a drag, but never a plain click */
    box.addEventListener('click', function (e) { if (moved) { e.stopPropagation(); e.preventDefault(); moved = false; } }, true);
  }

  function buildChips() {
    var chips = panel._chips; if (!chips) return;
    chips.innerHTML = '';
    var seen = {};
    Array.prototype.forEach.call(panel._body.querySelectorAll('.' + PREFIX + 'section[data-cat]'), function (sec) {
      var cat = sec.getAttribute('data-cat'); if (seen[cat]) return; seen[cat] = sec;
      var iconName = sec.getAttribute('data-icon');
      var chip = el('button', { type: 'button', 'class': PREFIX + 'chip', onClick: function () {
        var body = panel._body, top = sec.offsetTop - 6;
        if (typeof body.scrollTo === 'function') body.scrollTo({ top: top, behavior: 'smooth' }); else body.scrollTop = top;
        Array.prototype.forEach.call(chips.children, function (c) { c.classList.remove(PREFIX + 'cur'); });
        chip.classList.add(PREFIX + 'cur');
      } }, [iconName ? el('span', { html: ic(iconName) }) : null, el('span', { text: cat })]);
      chips.appendChild(chip);
    });
  }

  function applySearch(q) {
    if (!panel) return;
    q = (q || '').trim().toLowerCase();
    var body = panel._body, anyVisible = false;
    Array.prototype.forEach.call(body.querySelectorAll('.' + PREFIX + 'section'), function (sec) {
      var items = sec.querySelectorAll('[data-search]'), secHas = false;
      if (!items.length) { sec.style.display = q ? 'none' : ''; if (!q) { secHas = true; } }
      else {
        Array.prototype.forEach.call(items, function (it) {
          var match = !q || (it.getAttribute('data-search') || '').indexOf(q) > -1;
          it.style.display = match ? '' : 'none';
          if (match) secHas = true;
        });
        sec.style.display = secHas ? '' : 'none';
      }
      if (secHas) anyVisible = true;
    });
    if (panel._chips) panel._chips.style.display = q ? 'none' : '';
    if (panel._empty) panel._empty.classList.toggle(PREFIX + 'show', !!q && !anyVisible);
  }

  /* ================================================================
     RENDER ALL CONTROLS INTO THE PANEL BODY
     ================================================================ */
  function renderControls() {
    if (!panel) return;
    var f = config.features;
    var body = panel._body;
    body.innerHTML = '';

    var tt = panel.querySelector('.' + PREFIX + 'title-text');
    if (tt) tt.textContent = config.labels.panelTitle || t('panelTitle');
    var ts = panel.querySelector('.' + PREFIX + 'title-sub');
    if (ts) ts.textContent = t('customize');
    /* Refresh the static chrome (search, footer, close labels) from t() so it
       reverts correctly when switching back to the source language. */
    if (panel._search) { panel._search.setAttribute('placeholder', t('searchSettings')); panel._search.setAttribute('aria-label', t('searchSettings')); }
    if (panel._searchClear) panel._searchClear.setAttribute('aria-label', t('clear'));
    var rt = panel.querySelector('.' + PREFIX + 'reset-text'); if (rt) rt.textContent = t('reset');
    var dc = panel.querySelector('.' + PREFIX + 'disclaimer'); if (dc) dc.textContent = t('disclaimer');
    var hn = panel.querySelector('.' + PREFIX + 'hint'); if (hn) hn.textContent = t('shortcutHint');
    Array.prototype.forEach.call(panel.querySelectorAll('.' + PREFIX + 'close'), function (b) { b.setAttribute('aria-label', t('close')); });

    /* ---- Language ---- */
    if (f.languages) {
      var langKids = [buildLanguageSelector()];
      if (config.pageTranslation) langKids.push(el('p', { 'class': PREFIX + 'sec-sub', style: 'margin:10px 2px 0', text: t('translateNote') }));
      body.appendChild(makeSection('language', langKids, { id: 'language', cat: t('catLanguage'), icon: 'globe' }));
    }

    /* ---- Display: Oversized + Position ---- */
    var display = [];
    if (f.oversized) {
      var lab = el('div', { style: 'display:flex;align-items:center;gap:13px;flex:1 1 auto' }, [
        el('span', { 'class': PREFIX + 'xl-badge', 'aria-hidden': 'true', text: 'XL' }),
        el('div', {}, [
          el('div', { 'class': PREFIX + 'xl-lab', text: t('oversizedWidget') }),
          el('div', { 'class': PREFIX + 'xl-sub', text: t('oversizedSub') })
        ])
      ]);
      display.push(searchTag(makeSwitchRow(lab, prefs.oversized, function (on) { setPref('oversized', on); }), t('oversizedWidget')));
    }
    if (f.widgetPosition) {
      display.push(el('div', { style: 'margin-top:11px' }, [makeGrid([
        makeTile('position', null, prefs.widgetPosition === 'left', function () { setPref('widgetPosition', 'left'); }, { label: t('left') }),
        makeTile('position', null, prefs.widgetPosition === 'right', function () { setPref('widgetPosition', 'right'); }, { label: t('right') })
      ])]));
    }
    if (display.length) body.appendChild(makeSection('widgetSettings', display, { id: 'display', cat: t('catDisplay'), icon: 'scale' }));

    /* ---- Accessibility Profiles ---- */
    if (f.profiles) {
      var prows = [];
      PROFILE_ORDER.forEach(function (id) { prows.push(makeProfileRow(id)); });
      body.appendChild(makeSection('profiles', prows, { id: 'profiles', cat: t('catProfiles'), icon: 'accessibility', prominent: true, sub: 'profilesSub' }));
    }

    /* ---- Content Adjustments ---- */
    var content = [];
    if (f.contentScale) content.push(makeStepper('scale', 'contentScaling', prefs.contentScale + '%',
      function () { setPref('contentScale', Math.max(100, prefs.contentScale - 10)); },
      function () { setPref('contentScale', Math.min(200, prefs.contentScale + 10)); },
      prefs.contentScale <= 100, prefs.contentScale >= 200));
    if (f.textResize) content.push(makeStepper('biggerText', 'fontSizing', prefs.textResize + '%',
      function () { setPref('textResize', Math.max(100, prefs.textResize - 25)); },
      function () { setPref('textResize', Math.min(200, prefs.textResize + 25)); },
      prefs.textResize <= 100, prefs.textResize >= 200));
    if (f.lineHeight) {
      var lh = ['default', 'tall', 'taller', 'tallest']; var li = lh.indexOf(prefs.lineHeight);
      content.push(makeStepper('lineHeight', 'lineHeight', prefs.lineHeight === 'default' ? t('defaultLabel') : (li + 1) + 'x',
        function () { setPref('lineHeight', lh[Math.max(0, li - 1)]); },
        function () { setPref('lineHeight', lh[Math.min(lh.length - 1, li + 1)]); },
        li <= 0, li >= lh.length - 1));
    }
    if (f.letterSpacing) {
      var ls = ['default', 'wide', 'wider']; var lsi = ls.indexOf(prefs.letterSpacing);
      content.push(makeStepper('spacing', 'letterSpacing', prefs.letterSpacing === 'default' ? t('defaultLabel') : (lsi + 1) + 'x',
        function () { setPref('letterSpacing', ls[Math.max(0, lsi - 1)]); },
        function () { setPref('letterSpacing', ls[Math.min(ls.length - 1, lsi + 1)]); },
        lsi <= 0, lsi >= ls.length - 1));
    }
    var ctiles = [];
    if (f.readableFont) ctiles.push(makeTile('font', 'readableFont', prefs.readableFont, function () { toggleBool('readableFont'); }));
    if (f.dyslexiaFont) ctiles.push(makeTile('dyslexia', 'dyslexiaFont', prefs.dyslexiaFont, function () { toggleBool('dyslexiaFont'); }));
    if (f.highlightTitles) ctiles.push(makeTile('titles', 'highlightTitles', prefs.highlightTitles, function () { toggleBool('highlightTitles'); }));
    if (f.linkHighlight) ctiles.push(makeTile('link', 'highlightLinks', prefs.linkHighlight, function () { toggleBool('linkHighlight'); }));
    if (f.textMagnifier) ctiles.push(makeTile('magnifier', 'textMagnifier', prefs.textMagnifier, function () { toggleBool('textMagnifier'); }));
    if (f.textSpacing) {
      var sp = ['default', 'comfortable', 'spacious']; var spi = sp.indexOf(prefs.textSpacing); if (spi < 0) spi = 0;
      var spLabels = [t('normal'), t('comfortable'), t('spacious')];
      ctiles.push(makeCycleTile('spacing', 'textSpacing', sp, spi, spLabels, function () { setPref('textSpacing', sp[(spi + 1) % sp.length]); }));
    }
    if (f.textAlign) {
      ctiles.push(makeTile('alignLeft', 'alignLeft', prefs.textAlign === 'left', function () { toggleEnum('textAlign', 'left'); }));
      ctiles.push(makeTile('alignCenter', 'alignCenter', prefs.textAlign === 'center', function () { toggleEnum('textAlign', 'center'); }));
      ctiles.push(makeTile('alignRight', 'alignRight', prefs.textAlign === 'right', function () { toggleEnum('textAlign', 'right'); }));
    }
    if (content.length || ctiles.length) {
      var cWrap = content.slice();
      if (ctiles.length) cWrap.push(makeGrid(ctiles));
      body.appendChild(makeSection('contentAdj', cWrap, { id: 'content', cat: t('catContent'), icon: 'font' }));
    }

    /* ---- Color Adjustments ---- */
    var color = [];
    var colTiles = [];
    if (f.contrastModes) {
      colTiles.push(makeTile('moon', 'darkContrast', prefs.contrastMode === 'dark', function () { toggleEnum('contrastMode', 'dark'); }));
      colTiles.push(makeTile('sun', 'lightContrast', prefs.contrastMode === 'light', function () { toggleEnum('contrastMode', 'light'); }));
      colTiles.push(makeTile('contrast', 'highContrast', prefs.contrastMode === 'high-contrast', function () { toggleEnum('contrastMode', 'high-contrast'); }));
      colTiles.push(makeTile('contrast', 'smartContrast', prefs.contrastMode === 'smart-contrast', function () { toggleEnum('contrastMode', 'smart-contrast'); }));
      colTiles.push(makeTile('contrast', 'invertColors', prefs.contrastMode === 'inverted', function () { toggleEnum('contrastMode', 'inverted'); }));
    }
    if (f.saturation) {
      colTiles.push(makeTile('saturation', 'highSaturation', prefs.saturation === 'high', function () { toggleEnum('saturation', 'high'); }));
      colTiles.push(makeTile('saturation', 'lowSaturation', prefs.saturation === 'low', function () { toggleEnum('saturation', 'low'); }));
      colTiles.push(makeTile('saturation', 'monochrome', prefs.saturation === 'monochrome', function () { toggleEnum('saturation', 'monochrome'); }));
    }
    if (f.colorBlind) {
      colTiles.push(makeTile('palette', 'protanopia', prefs.colorBlindFilter === 'protanopia', function () { toggleEnum('colorBlindFilter', 'protanopia'); }));
      colTiles.push(makeTile('palette', 'deuteranopia', prefs.colorBlindFilter === 'deuteranopia', function () { toggleEnum('colorBlindFilter', 'deuteranopia'); }));
      colTiles.push(makeTile('palette', 'tritanopia', prefs.colorBlindFilter === 'tritanopia', function () { toggleEnum('colorBlindFilter', 'tritanopia'); }));
    }
    if (colTiles.length) color.push(makeGrid(colTiles));
    if (f.contrastBoost) color.push(makeStepper('contrastPlus', 'contrastPlus', prefs.contrastBoost === 0 ? t('defaultLabel') : '+' + prefs.contrastBoost,
      function () { setPref('contrastBoost', Math.max(0, prefs.contrastBoost - 1)); },
      function () { setPref('contrastBoost', Math.min(3, prefs.contrastBoost + 1)); },
      prefs.contrastBoost <= 0, prefs.contrastBoost >= 3));
    if (f.customColors) {
      color.push(makeColorPicker('adjustTextColors', prefs.textColor, function (h) { setPref('textColor', h); }, function () { setPref('textColor', ''); }));
      color.push(makeColorPicker('adjustTitleColors', prefs.titleColor, function (h) { setPref('titleColor', h); }, function () { setPref('titleColor', ''); }));
      color.push(makeColorPicker('adjustBgColors', prefs.bgColor, function (h) { setPref('bgColor', h); }, function () { setPref('bgColor', ''); }));
    }
    if (color.length) body.appendChild(makeSection('colorAdj', color, { id: 'color', cat: t('catColor'), icon: 'palette' }));

    /* ---- Orientation / Reading ---- */
    var orient = [];
    var oTiles = [];
    if (f.muteSounds) oTiles.push(makeTile('mute', 'muteSounds', prefs.muteSounds, function () { toggleBool('muteSounds'); }));
    if (f.hideImages) oTiles.push(makeTile('hideImg', 'hideImages', prefs.hideImages, function () { toggleBool('hideImages'); }));
    if (f.readMode) oTiles.push(makeTile('readMode', 'readMode', prefs.readMode, function () { toggleBool('readMode'); }));
    if (f.readingGuide) oTiles.push(makeTile('guide', 'readingGuide', prefs.readingGuide, function () { toggleBool('readingGuide'); }));
    if (f.readingMask) oTiles.push(makeTile('mask', 'readingMask', prefs.readingMask, function () { toggleBool('readingMask'); }));
    if (f.stopAnimations) oTiles.push(makeTile('stop', 'stopAnimations', prefs.stopAnimations, function () { toggleBool('stopAnimations'); }));
    if (f.highlightHover) oTiles.push(makeTile('hover', 'highlightHover', prefs.highlightHover, function () { toggleBool('highlightHover'); }));
    if (f.highlightFocus) oTiles.push(makeTile('focus', 'highlightFocus', prefs.highlightFocus, function () { toggleBool('highlightFocus'); }));
    if (f.tooltips) oTiles.push(makeTile('tooltip', 'tooltips', prefs.tooltips, function () { toggleBool('tooltips'); }));
    if (f.bigCursor) {
      oTiles.push(makeTile('cursorBlack', 'bigBlackCursor', prefs.cursorStyle === 'big-black', function () { toggleEnum('cursorStyle', 'big-black'); }));
      oTiles.push(makeTile('cursorWhite', 'bigWhiteCursor', prefs.cursorStyle === 'big-white', function () { toggleEnum('cursorStyle', 'big-white'); }));
    }
    if (f.pageStructure) oTiles.push(makeTile('structure', 'pageStructure', false, openStructure));
    if (f.dictionary && config.dictionaryEndpoint) oTiles.push(makeTile('dictionary', 'dictionary', prefs.dictionary, function () { toggleBool('dictionary'); }));
    if (oTiles.length) orient.push(makeGrid(oTiles));
    if (orient.length) body.appendChild(makeSection('orientationAdj', orient, { id: 'reading', cat: t('catReading'), icon: 'readMode' }));

    /* ---- Assistive Technology ---- */
    var assist = [];
    if (f.screenReader) assist.push(makeTile('speaker', 'screenReaderTTS', prefs.screenReader, function () { toggleBool('screenReader'); }));
    if (f.voiceNavigation) assist.push(makeTile('mic', 'voiceNavigation', prefs.voiceNavigation, function () { toggleBool('voiceNavigation'); }));
    if (f.keyboardNav) assist.push(makeTile('keyboard', 'keyboardNavigation', prefs.keyboardNav, function () { toggleBool('keyboardNav'); }));
    if (assist.length) body.appendChild(makeSection('assistiveTech', [makeGrid(assist)], { id: 'assistive', cat: t('catTools'), icon: 'sliders' }));

    /* ---- Useful Links (prominent, own section) ---- */
    if (f.usefulLinks !== false && config.usefulLinks && config.usefulLinks.length) {
      body.appendChild(makeSection('usefulLinks', [buildUsefulLinks()], { id: 'links', cat: t('catTools'), icon: 'link', prominent: true }));
    }

    /* rebuild chip nav + re-apply any active search filter */
    buildChips();
    if (panel._search && panel._search.value) applySearch(panel._search.value);
    /* machine-translate any widget text not covered by a hand-written locale */
    if (config.pageTranslation && prefs.language) scheduleWidgetUI(prefs.language);
  }
  /* ================================================================
     SHORTCUT LABEL (for tooltips / hints)
     ================================================================ */
  function shortcutLabel() {
    switch (config.openShortcut) {
      case 'double-shift': return 'Shift Shift';
      case 'alt-a':        return 'Alt + A';
      case 'ctrl-alt-a':   return 'Ctrl + Alt + A';
      default:             return 'Alt + A';
    }
  }

  /* ================================================================
     OPEN / CLOSE PANEL
     ================================================================ */
  function openPanel() {
    if (!panel || panelOpen) return;
    panelOpen = true;
    panel.classList.add(PREFIX + 'open');
    panel.setAttribute('aria-hidden', 'false');
    if (widgetRoot._backdrop) widgetRoot._backdrop.classList.add(PREFIX + 'open');
    widgetRoot.classList.add(PREFIX + 'panel-open');
    triggerBtn.setAttribute('aria-expanded', 'true');
    var closeBtn = panel.querySelector('.' + PREFIX + 'close');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 60);
    document.addEventListener('keydown', onPanelKeydown, true);
  }
  function closePanel() {
    if (!panel || !panelOpen) return;
    panelOpen = false;
    panel.classList.remove(PREFIX + 'open');
    panel.setAttribute('aria-hidden', 'true');
    if (widgetRoot._backdrop) widgetRoot._backdrop.classList.remove(PREFIX + 'open');
    widgetRoot.classList.remove(PREFIX + 'panel-open');
    triggerBtn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onPanelKeydown, true);
    try { triggerBtn.focus(); } catch (e) {}
  }
  function togglePanel() { panelOpen ? closePanel() : openPanel(); }

  /* Simple focus containment + Escape while the panel is open */
  function onPanelKeydown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      if (structDialog && structDialog.classList.contains(PREFIX + 'open')) { closeStructure(); return; }
      e.preventDefault(); closePanel(); return;
    }
    if (e.key === 'Tab') {
      var focusables = panel.querySelectorAll('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])');
      focusables = Array.prototype.filter.call(focusables, function (n) { return n.offsetParent !== null || n === document.activeElement; });
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  /* ================================================================
     GLOBAL KEYBOARD SHORTCUTS
       • open/close   → configurable (default: press Shift twice)
       • Alt + G      → toggle Reading Guide (kept from v1)
     The double-Shift detector ignores the case where Shift is held with
     another key (so it never fires while someone is capitalising text or
     using a screen-reader pass-through command).
     ================================================================ */
  function onGlobalKeydown(e) {
    /* Reading guide (existing v1 binding) */
    if (e.altKey && (e.key === 'g' || e.key === 'G') && !e.ctrlKey && !e.metaKey) {
      e.preventDefault(); setPref('readingGuide', !prefs.readingGuide);
      announceToggle(t('readingGuide'), prefs.readingGuide); return;
    }
    var sc = config.openShortcut;
    if (!sc) return;
    if (sc === 'alt-a' && e.altKey && !e.ctrlKey && !e.metaKey && (e.key === 'a' || e.key === 'A')) { e.preventDefault(); togglePanel(); return; }
    if (sc === 'ctrl-alt-a' && e.ctrlKey && e.altKey && (e.key === 'a' || e.key === 'A')) { e.preventDefault(); togglePanel(); return; }
    if (sc === 'double-shift') {
      if (e.key === 'Shift' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        var now = Date.now();
        if (now - lastShiftTime < 400) { lastShiftTime = 0; togglePanel(); }
        else { lastShiftTime = now; }
      } else if (e.key !== 'Shift') {
        lastShiftTime = 0; /* any other key cancels a pending double-tap */
      }
    }
  }

  /* ================================================================
     INITIALISATION
     ================================================================ */
  var initialised = false;
  function init() {
    if (initialised) return;
    initialised = true;
    loadPrefs();
    buildWidget();
    injectWidgetStyles();
    renderControls();
    applyAll();
    applyLanguage();
    translatePage(prefs.language);
    document.addEventListener('keydown', onGlobalKeydown, true);
    if (typeof config.onInit === 'function') { try { config.onInit(getPrefsCopy()); } catch (e) {} }
  }

  function getPrefsCopy() { return JSON.parse(JSON.stringify(prefs)); }

  /* ================================================================
     PUBLIC API
     ================================================================ */
  window.AccessibilityWidget = {
    open:    openPanel,
    close:   closePanel,
    toggle:  togglePanel,
    getPrefs: getPrefsCopy,
    setPreference: function (key, value) {
      if (key in defaults) { setPref(key, value); }
    },
    setLanguage: function (code) {
      if (langMeta(code).code === code) { setPref('language', code); applyLanguage(); translatePage(code); renderControls(); }
    },
    toggleProfile: function (id, on) { toggleProfile(id, on !== false); },
    retranslate: function () { if (panel) translatePage(prefs.language, true); },
    reset: function () { resetPrefs(); applyAll(); applyLanguage(); if (panel) renderControls(); },
    init: init,
    version: '2.0.0'
  };

  /* ================================================================
     EARLY APPLY + BOOTSTRAP
     We load saved preferences and pre-apply visual filters to <body> as
     soon as the DOM is interactive to minimise any flash of the default
     theme, then build the full widget.
     ================================================================ */
  function preApply() {
    try {
      loadPrefs();
      if (document.body) { applyContrastMode(); applyColorFilters(); applyTextResize(); applyContentScale(); }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    preApply();
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
